import React, { useState, useEffect } from "react"; 

import {
    MapPin, MessageSquare, Users, MousePointer, Radio, Newspaper, Smartphone, Phone,
    ChevronRight, ChevronDown, Mail, Activity, Lightbulb, Zap, Shield, Info, Download,
    FileText, Target, DollarSign, TrendingUp, Check, XCircle, Cpu, Key, BarChart, Percent,
    UserPlus, Heart, ArrowUpCircle, AlertTriangle, Grid,
    TrendingDown, Smile, Crosshair, Flag,
    Megaphone, CheckSquare,
    Link, Diamond, Layers, Scale,
    BatteryWarning,
    Wind, Brain, Gauge, BarChart2, PieChart,
    Clock, Briefcase, Timer, ChevronUp,
    Sparkles, Wallet, ArrowRight, Calendar,
    BookOpen, Eye, GitMerge, AlertOctagon,
    Fingerprint, Trash2, ArrowLeft
} from "lucide-react";

import ARCHETYPE_DATA from "../data/archetype_summary.json";
import AGE_DATA from "../data/age_summary.json";

// ================================================================================
// START: HELPER COMPONENTS & LOGIC
// ================================================================================

// Helper: Format channel names
const formatChannelName = (name) => {
    if (!name) return 'N/A';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Helper: Format Date for HUD
const formatFriendlyDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'short' 
        });
    } catch (e) {
        return 'N/A';
    }
};

// Helper: Format Execution Windows
const formatExecutionDates = (start, end) => {
    if (!start || !end) return 'N/A';
    return `${formatFriendlyDate(start)} → ${formatFriendlyDate(end)}`;
};

// Helper: Format Currency or Time based on Mode
const formatResourceValue = (budget, hours, mode) => {
    if (mode === 'time_only') return `${hours ? hours.toFixed(0) : 0} hrs`;
    if (mode === 'hybrid') return `£${budget ? budget.toLocaleString() : 0} + ${hours ? hours.toFixed(0) : 0} hrs`;
    return `£${budget ? budget.toLocaleString() : 0}`;
};

// Helper: Get channel icons
const getChannelIcon = (channel) => {
    const c = (channel ?? '').toLowerCase();
    if (c.includes('door') || c.includes('canvass')) return <Users size={24} />;
    if (c.includes('digital')) return <MousePointer size={24} />;
    if (c.includes('postal')) return <Mail size={24} />;
    if (c.includes('social')) return <MessageSquare size={24} />;
    if (c.includes('radio')) return <Radio size={24} />;
    if (c.includes('newspaper')) return <Newspaper size={24} />;
    if (c.includes('text')) return <Smartphone size={24} />;
    if (c.includes('phone')) return <Phone size={24} />;
    if (c.includes('event')) return <Calendar size={24} />;
    return <Activity size={24} />;
};

// Helper: Small stat component
const WardStat = ({ icon, label, value }) => (
    <div className="flex items-center space-x-2 bg-slate-800/50 p-2 rounded-lg border border-slate-700/30">
        <div className="text-accent-400">{icon}</div>
        <div className="overflow-hidden">
            <div className="text-xs text-slate-400 truncate">{label}</div>
            <div className="text-sm font-semibold text-white truncate" title={value}>{value ?? 'N/A'}</div>
        </div>
    </div>
);

// Helper: Grade badge (MODERNIZED)
const GradeBadge = ({ grade }) => {
    const config = {
        'A+': { color: 'text-cyan-400', glow: 'shadow-[0_0_30px_-5px_rgba(34,211,238,0.3)]', stroke: 'stroke-cyan-400' },
        'A': { color: 'text-emerald-400', glow: 'shadow-[0_0_30px_-5px_rgba(52,211,153,0.3)]', stroke: 'stroke-emerald-400' },
        'B': { color: 'text-yellow-400', glow: 'shadow-[0_0_30px_-5px_rgba(250,204,21,0.3)]', stroke: 'stroke-yellow-400' },
        'C': { color: 'text-orange-400', glow: 'shadow-[0_0_30px_-5px_rgba(251,146,60,0.3)]', stroke: 'stroke-orange-400' },
        'D': { color: 'text-red-400', glow: 'shadow-[0_0_30px_-5px_rgba(248,113,113,0.3)]', stroke: 'stroke-red-400' },
        'F': { color: 'text-red-600', glow: 'shadow-[0_0_30px_-5px_rgba(220,38,38,0.3)]', stroke: 'stroke-red-600' },
        'N/A': { color: 'text-slate-500', glow: '', stroke: 'stroke-slate-600' }
    }[grade] || { color: 'text-slate-500', glow: '', stroke: 'stroke-slate-600' };

    return (
        <div className={`relative flex flex-col items-center justify-center p-4 rounded-xl border border-white/5 bg-slate-900/40 backdrop-blur-xl ${config.glow} overflow-hidden`}>
            {/* Background Gradient Hint */}
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none`}></div>
            
            <div className="absolute top-3 left-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Strategy Grade</div>
            
            <div className="relative w-20 h-20 mt-4 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    {/* Background Circle */}
                    <path className="text-slate-800" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" strokeWidth="2.5" />
                    {/* Foreground Circle */}
                    <path 
                        className={`${config.stroke} transition-all duration-1000 ease-out`}
                        strokeDasharray="100, 100"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
                        fill="none" 
                        strokeWidth="3" 
                        strokeLinecap="round" 
                    />
                </svg>
                <div className={`absolute text-3xl font-black ${config.color} tracking-tighter`}>{grade}</div>
            </div>
            
            <div className="mt-2 text-[10px] text-slate-400 font-medium">Plato Analysis</div>
        </div>
    );
};

// --- NEW COMPONENT: Modern Progress Bar (Aesthetic Port) ---
const ModernProgressBar = ({ label, score, colorClass = "bg-accent-500" }) => (
    <div className="w-full group">
        <div className="flex justify-between items-end text-xs mb-1.5">
            <span className="text-slate-400 font-medium group-hover:text-white transition-colors">{label}</span>
            <span className="font-mono text-white font-bold">{(score ?? 0).toFixed(0)}%</span>
        </div>
        <div className="w-full bg-slate-700/30 rounded-full h-1.5 overflow-hidden border border-white/5">
            <div 
                className={`h-full rounded-full shadow-[0_0_10px_rgba(0,0,0,0.3)] transition-all duration-1000 ease-out ${colorClass}`} 
                style={{ width: `${Math.min(score ?? 0, 100)}%` }} 
            />
        </div>
    </div>
);

// --- NEW COMPONENT: Modern Mini Gauge (For List Views) ---
const ModernMiniGauge = ({ score, gradientClass }) => (
    <div className="w-full h-1.5 bg-slate-700/30 rounded-full overflow-hidden border border-white/5 mt-1.5">
        <div 
            className={`h-full rounded-full shadow-sm transition-all duration-1000 ease-out ${gradientClass}`} 
            style={{ width: `${Math.min(score ?? 0, 100)}%` }} 
        />
    </div>
);

// Helper: Math std dev
const getStdDev = (arr) => {
    if (!arr || arr.length === 0) return 0;
    const mean = arr.reduce((a, b) => a + b) / arr.length;
    return Math.sqrt(arr.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / arr.length);
};

// Helper: Front-end mirror of engine's archetype age logic
const ARCHETYPE_AGE_DEFAULTS = {
    'young_professional_urban': {'18-24': 0.22, '25-39': 0.38, '40-59': 0.28, '60+': 0.12},
    'established_suburban_family': {'18-24': 0.08, '25-39': 0.22, '40-59': 0.35, '60+': 0.35},
    'retirement_community': {'18-24': 0.05, '25-39': 0.15, '40-59': 0.25, '60+': 0.55},
    'mixed_demographic': {'18-24': 0.12, '25-39': 0.28, '40-59': 0.35, '60+': 0.25},
    'working_class_traditional': {'18-24': 0.14, '25-39': 0.26, '40-59': 0.38, '60+': 0.22}
};

// Helper: Get archetype age map
const getArchetypeAgeMap = (archetypeName) => {
    const name = (archetypeName || '').toLowerCase();
    const archetypeMap = {
        "civic pragmatist": "established_suburban_family", "graduate professional": "young_professional_urban", 
        "working class traditional": "working_class_traditional", "established professionals": "established_suburban_family", 
        "backbone conservative core": "retirement_community", "well-off traditionalist": "established_suburban_family", 
        "well-off traditionalist conservative": "retirement_community"
    };
    const mappedName = archetypeMap[name] || "mixed_demographic";
    return ARCHETYPE_AGE_DEFAULTS[mappedName] || ARCHETYPE_AGE_DEFAULTS['mixed_demographic'];
};

// Helper: Calculate archetype fit
const calculateArchetypeFit = (archetypeMix, channelEffectiveness) => {
    if (!archetypeMix || !channelEffectiveness) return [];
    const effectivenessScores = {};
    Object.entries(channelEffectiveness.age_group_contributions ?? {}).forEach(([age, data]) => {
        effectivenessScores[age] = data.effectiveness_score;
    });
    return archetypeMix.map(archetype => {
        const ageMap = getArchetypeAgeMap(archetype.name);
        let fitScore = 0;
        Object.entries(ageMap).forEach(([age, pct]) => { fitScore += (pct * (effectivenessScores[age] ?? 0)); });
        return { 
            name: archetype.name, 
            percentage: archetype.percentage, 
            fitScore: fitScore * 100 
        };
    }).sort((a, b) => b.percentage - a.percentage);
};

// Helper: Get Strategic Driver
const getStrategicDriver = (channels) => {
    if (!channels || channels.length === 0) return { driver: "N/A", rationale: "No channel data to analyze." };
    const fits = channels.map(c => (c.average_demographic_fit ?? 0) * 100);
    const costs = channels.map(c => c.average_shadow_cpv || c.average_cost_per_vote).filter(c => c > 0 && c !== Infinity);
    
    if (costs.length === 0) return { driver: "Fit-Driven", rationale: "Allocation is based purely on demographic fit." };
    const fitStdDev = getStdDev(fits);
    const costStdDev = getStdDev(costs.map(c => 1 / c)); 
    if (costStdDev > fitStdDev * 1.5) return { driver: "Resource-Driven", rationale: "Resource efficiency varies significantly. Model prioritized high-ROI (Time or Money) options." };
    if (fitStdDev > costStdDev * 1.5) return { driver: "Fit-Driven", rationale: "Demographic Fit varies significantly. Model prioritized alignment with ward archetypes." };
    return { driver: "Balanced", rationale: "Both resource efficiency and demographic fit are balanced." };
};

// Helper: Strategic Alignment
const getStrategicAlignment = (artemisTier, campaignFocus) => {
    if (!artemisTier || !campaignFocus) return { level: 'Medium', label: 'N/A', rationale: 'Missing data for alignment check.' };
    const tier = artemisTier.toUpperCase();
    const focus = campaignFocus.toUpperCase();
    const isGain = tier.includes('GAIN');
    const isDefend = tier.includes('DEFEND') || tier.includes('SAFE');
    const isSwingFocus = focus.includes('SWING') || focus.includes('LATE_DECIDER') || focus.includes('PERSUASION');
    const isBaseFocus = focus.includes('CORE') || focus.includes('TURNOUT') || focus.includes('RETENTION');

    if (isGain && isSwingFocus) return { level: 'High', label: 'Synergistic', rationale: 'Aggressive growth strategy matches Gain opportunity.' };
    if (isDefend && isBaseFocus) return { level: 'High', label: 'Synergistic', rationale: 'Defensive resource allocation matches retention need.' };
    if (isGain && isBaseFocus) return { level: 'Low', label: 'Strategic Friction', rationale: 'Using defensive tactics on a Gain target. Pivot to persuasion recommended.' };
    if (isDefend && isSwingFocus) return { level: 'Medium', label: 'Review Needed', rationale: 'Using persuasion on a Defend seat. Ensure base is secure first.' };
    return { level: 'Medium', label: 'Neutral', rationale: 'No strong conflict or synergy detected. Monitor efficiency.' };
};

// Helper: Untapped Potential (Fixed for Pre-calculated Percentages)
const getUntappedPotential = (tacticalIntelligence) => {
    const sensitivities = tacticalIntelligence?.sensitivities ?? [];
    
    if (!Array.isArray(sensitivities) || sensitivities.length === 0) {
        return { level: 'N/A', rationale: 'Sensitivity data unavailable.', percent: 0 };
    }

    const bestOpp = sensitivities[0]; 
    if (!bestOpp) return { level: 'N/A', rationale: 'Sensitivity data unavailable.', percent: 0 };

    const rawElasticity = bestOpp.elasticity;
    const elasticity = (rawElasticity !== undefined && rawElasticity !== null && !isNaN(rawElasticity)) ? Number(rawElasticity) : 0;
    
    const percent = elasticity > 1.0 ? elasticity : elasticity * 100;
    const channelName = formatChannelName(bestOpp.channel || 'Unknown Channel');

    // Thresholds adjusted for percentage values
    const thresholdHigh = 50;  // 50% or 0.5
    const thresholdMed = 20;   // 20% or 0.2

    if (percent > thresholdHigh) return { level: 'High', rationale: `Spending more on ${channelName} yields significant marginal gains.`, percent };
    if (percent > thresholdMed) return { level: 'Medium', rationale: `Moderate gains possible by increasing ${channelName} budget.`, percent };
    
    return { level: 'Low', rationale: 'Diminishing returns detected. Budget is near saturation point.', percent };
};

// Helper: Generate Demographic Rationale (Frontend Synthesis)
const generateDemographicInsights = (demoAnalysis) => {
    if (!demoAnalysis?.age_group_contributions) return { target: 'N/A', rationale: [] };

    const entries = Object.entries(demoAnalysis.age_group_contributions);
    if (entries.length === 0) return { target: 'N/A', rationale: [] };

    const best = entries.reduce((max, curr) => (curr[1].contribution_percentage > max[1].contribution_percentage ? curr : max));
    const bestAge = best[0];
    
    const rationale = [];
    rationale.push(`Primary driver is the ${bestAge} demographic (${best[1].contribution_percentage.toFixed(0)}% of impact).`);
    
    if (bestAge === '18-24') rationale.push("High youth density suggests digital/mobile-first effectiveness.");
    else if (bestAge === '25-39') rationale.push("Working-age population responsive to convenience and lifestyle messaging.");
    else if (bestAge === '40-59') rationale.push("Established families/professionals drive this channel's performance.");
    else if (bestAge === '60+') rationale.push("Senior demographic alignment indicates high responsiveness to traditional/postal contact.");

    return { target: bestAge, rationale };
};

// Synthesizes the Social Dynamics coefficient into a narrative
const getSocialAtmosphere = (socialScore, strategicScore, tier) => {
    const t = (tier || '').toUpperCase();
    const isChallenger = t.includes('GAIN') || t.includes('TARGET') || t.includes('UNWINNABLE');

    if (socialScore === undefined) return { label: "Neutral", desc: "No significant social pressure detected.", color: "text-slate-400", icon: <Wind size={20}/> };
    
    if (socialScore > 0.015) {
        if (isChallenger) {
             return { label: "Underdog Surge", desc: "Challenger momentum is building. Voters are breaking for the rising alternative.", color: "text-cyan-400", icon: <TrendingUp size={20}/> };
        }
        return { label: "Bandwagon Effect", desc: "Incumbency strength is amplifying support. Voters are rallying around the perceived winner.", color: "text-success-400", icon: <Users size={20}/> };
    }
    
    if (socialScore < -0.01) return { label: "Spiral of Silence", desc: "Dominant opposing narratives are suppressing visible support. Expect 'shy' voters.", color: "text-orange-400", icon: <MessageSquare size={20}/> };
    
    if (strategicScore > 0.01) return { label: "Tactical Consolidation", desc: "High-information voters are consolidating behind this party to block a rival.", color: "text-purple-400", icon: <Crosshair size={20}/> };

    return { label: "Stable Atmosphere", desc: "Local dynamics are insulated from national volatility.", color: "text-slate-400", icon: <Wind size={20}/> };
};

// Synthesizes IRT (Item Response Theory)
const getInteractionPsychometrics = (metrics) => {
    const irt = metrics?.irt_contact_intensity ?? 0.5;
    const difficulty = irt < 0.3 ? "High Barrier" : irt < 0.6 ? "Moderate Friction" : "Low Resistance";
    let narrative = "";
    if (irt < 0.3) narrative = "Voters are resistant to this channel. Requires high-quality, 'soft' messaging to break through.";
    else if (irt > 0.7) narrative = "Open door environment. Voters are predisposed to engage via this medium.";
    else narrative = "Standard engagement friction. Success depends heavily on message personalization.";
    return { level: difficulty, narrative, score: irt * 100 };
};

// Synthesizes Campaign Phase Logic
const getPhaseAnalysis = (days, volatility) => {
    const volPct = (volatility * 100).toFixed(0);
    if (days < 7) return { label: "Final Frenzy", desc: `Maximum volatility (${volPct}%). GOTV is the only priority.`, urgency: "Critical" };
    if (days < 30) return { label: "Persuasion Window", desc: `High elasticity. Swing voters are deciding now.`, urgency: "High" };
    return { label: "Foundation Building", desc: "Low volatility. Focus on identification and awareness.", urgency: "Medium" };
};

// ================================================================================
// NEW COMPONENT: Strategic Leverage Gauge (Diamond Data Visualization)
// ================================================================================

const StrategicLeverageGauge = ({ leverageData }) => {
    if (!leverageData) return null;

    const { leverage_score, leverage_status, projected_vote_lift, votes_needed_to_win, rationale } = leverageData;
    const percentage = Math.min(leverage_score * 100, 100);

    const getStatusConfig = () => {
        const styles = {
            'WIN_PROJECTED':     { label: 'Win Projected',     color: 'text-emerald-400', bg: 'bg-emerald-500', border: 'border-emerald-500/30', icon: <CheckSquare size={16}/> },
            'COMPETITIVE':       { label: 'Competitive',       color: 'text-cyan-400',    bg: 'bg-cyan-500',    border: 'border-cyan-500/30',    icon: <TrendingUp size={16}/> },
            'CONTRIBUTING':      { label: 'Contributing',      color: 'text-yellow-400',  bg: 'bg-yellow-500',  border: 'border-yellow-500/30',  icon: <Activity size={16}/> },
            'INEFFICIENT':       { label: 'Inefficient',       color: 'text-red-400',     bg: 'bg-red-500',     border: 'border-red-500/30',     icon: <AlertTriangle size={16}/> },
            'HIGH_IMPACT_EVENT': { label: 'High Impact Event', color: 'text-amber-400',   bg: 'bg-amber-500',   border: 'border-amber-500/30',   icon: <Zap size={16}/> },
            'NEUTRAL':           { label: 'Neutral',           color: 'text-slate-400',   bg: 'bg-slate-500',   border: 'border-slate-500/30',   icon: <Info size={16}/> }
        };

        if (leverage_status !== 'INEFFICIENT' && percentage > 0 && percentage < 15) {
            return styles['CONTRIBUTING']; 
        }

        if (styles[leverage_status]) {
            return styles[leverage_status];
        }

        if (percentage >= 100) return styles['WIN_PROJECTED'];
        if (percentage >= 50)  return styles['COMPETITIVE'];
        if (percentage >= 15)  return styles['HIGH_IMPACT_EVENT'];
        if (percentage > 0)    return styles['CONTRIBUTING'];
        
        return styles['NEUTRAL'];
    };

    const statusConfig = getStatusConfig();

    return (
        <div className={`relative overflow-hidden rounded-xl bg-slate-800/60 border ${statusConfig.border} p-5 mb-6`}>
            {/* Background Glow */}
            <div className={`absolute top-0 right-0 w-32 h-32 ${statusConfig.bg} opacity-5 blur-3xl rounded-full -mr-10 -mt-10 pointer-events-none`}></div>

            <div className="flex justify-between items-start mb-4 relative z-10">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className={`p-1.5 rounded-md bg-slate-900/50 ${statusConfig.color} border border-white/5`}>
                            {statusConfig.icon}
                        </div>
                        <h5 className="text-sm font-bold text-white uppercase tracking-wider">Strategic Leverage</h5>
                    </div>
                    <p className="text-xs text-slate-400">Campaign Lift vs. Win Requirement</p>
                </div>
                <div className="text-right">
                    <div className={`text-2xl font-black ${statusConfig.color} font-mono`}>
                        {percentage.toFixed(1)}%
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Gap Closed</div>
                </div>
            </div>

            {/* Progress Bar Container */}
            <div className="relative w-full h-3 bg-slate-900/80 rounded-full overflow-hidden mb-3 border border-slate-700/50">
                {/* Target Marker (The Win Line) */}
                <div className="absolute top-0 bottom-0 w-0.5 bg-white/30 z-20 left-[100%]"></div>
                
                {/* Progress Fill */}
                <div 
                    className={`h-full ${statusConfig.bg} transition-all duration-1000 ease-out relative z-10`} 
                    style={{ width: `${Math.max(percentage, 2)}%` }}
                >
                    {/* Animated Shine Effect */}
                    <div className="absolute top-0 bottom-0 right-0 w-full bg-gradient-to-l from-white/20 to-transparent"></div>
                </div>
            </div>

            {/* Metrics Row */}
            <div className="flex justify-between items-center text-xs relative z-10 border-t border-slate-700/50 pt-3 mt-3">
                <div className="flex flex-col">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Projected Lift</span>
                    <span className={`font-mono font-bold ${statusConfig.color}`}>+{projected_vote_lift.toLocaleString()} Votes</span>
                </div>
                <div className="flex flex-col items-center">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Status</span>
                    <span className={`font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Required Swing</span>
                    <span className="font-mono font-bold text-slate-300">{votes_needed_to_win.toLocaleString()} Votes</span>
                </div>
            </div>
            
            {/* AI Rationale */}
            <div className="mt-3 bg-slate-900/40 rounded p-2 border border-white/5 text-xs text-slate-300 italic leading-relaxed">
                "{rationale}"
            </div>
        </div>
    );
};


// ================================================================================
// START: MAIN COMPONENT (SIMULATED CAMPAIGN SUMMARY)
// ================================================================================

export const SimulatedCampaignSummary = ({ data, onDelete, onBack }) => {
    // --- VITAL FIX: Bridge 'data' to 'results' ---
    const results = data;
    const campaignInput = results?.input_summary;

    const [channelViewMode, setChannelViewMode] = useState({});
    
    // --- MULTI-WARD STATE MANAGEMENT ---
    const [selectedWardId, setSelectedWardId] = useState(null);
    const [isAllocationsExpanded, setIsAllocationsExpanded] = useState(true);
    const [activeArchetype, setActiveArchetype] = useState(null);
    const [activeAgeGroup, setActiveAgeGroup] = useState(null);   // <--- ADD THIS LINE
    const [ageGenderToggle, setAgeGenderToggle] = useState('female');
    
    // --- STRATEGIC TABS STATE ---
    const [activeStrategicTab, setActiveStrategicTab] = useState(null);

    // --- TOUCHPOINT VIEW STATE ---
    const [touchpointView, setTouchpointView] = useState('ward'); 

    const [selectedCanvassWeek, setSelectedCanvassWeek] = useState(null);
    const [selectedPulseDay, setSelectedPulseDay] = useState(null);

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [openChannel, setOpenChannel] = useState(null);
    
    // --- Safely access all data ---
    // Robust Ward ID selection. Get all keys.
    const wardKeys = Object.keys(results?.ward_level_analysis ?? {});
    
    // Effect: Initialize selectedWardId if not set or if results change
    useEffect(() => {
        if (wardKeys.length > 0 && !selectedWardId) {
            setSelectedWardId(wardKeys[0]);
        } else if (wardKeys.length > 0 && !wardKeys.includes(selectedWardId)) {
            // If current selection is invalid (e.g. new result set), reset to first
            setSelectedWardId(wardKeys[0]);
        }
    }, [results, wardKeys, selectedWardId]);

    // Derived State: Current Ward Data
    const currentWardId = selectedWardId || (wardKeys.length > 0 ? wardKeys[0] : null);
    const wardAnalysis = currentWardId ? results?.ward_level_analysis?.[currentWardId] : null;
    const wardContext = wardAnalysis?.ward_contextual_intelligence;

    // Derived State: Resonance Data
    const resonanceData = results?.resonance_strategic_overlay;
    const currentWardResonance = resonanceData?.ward_level_resonance?.[currentWardId];
    
    const performance = results?.performance_dashboard;
    const campaignMetrics = results?.campaign_optimization?.campaign_efficiency_metrics;
    const monteCarloProjections = results?.monte_carlo_simulation?.vote_projections;
    const monteCarloRisks = results?.monte_carlo_simulation?.risk_metrics;
    const tacticalRecs = results?.strategic_recommendations?.tactical_recommendations ?? [];
    const expansionOps = results?.campaign_optimization?.budget_expansion_opportunities;

    // Fix: Dual access for tactical_intelligence to handle snake_case (Python) vs camelCase (JS)
    const tacticalIntelligence = results?.tactical_intelligence || results?.tacticalIntelligence;
    
    // --- Call Synthesis Helpers ---
    const allRecs = results?.campaign_optimization?.recommended_channels ?? [];
    
    // Determine cost range specifically for financial channels to normalize bars
    const financialRecs = allRecs.filter(r => r.mode !== 'time_only');
    const costs = financialRecs.map(r => r.average_cost_per_vote).filter(c => c > 0 && c !== Infinity);
    const minCost = costs.length > 0 ? Math.min(...costs) : 0;
    const maxCost = costs.length > 0 ? Math.max(...costs) : 0;
    const costRange = maxCost - minCost;
    
    const recommendedChannelNames = allRecs.slice(0, 3).map(r => r.channel);
    const confidenceWarnings = recommendedChannelNames.map(channelName => {
        const channelData = wardAnalysis?.channel_performance?.[channelName];
        if (channelData && channelData.confidence_score < 0.7) return `The projection for ${formatChannelName(channelName)} is based on limited data (Conf: ${(channelData.confidence_score * 100).toFixed(0)}%). Exercise caution.`;
        return null;
    }).filter(Boolean);

    const { driver, rationale: driverRationale } = getStrategicDriver(allRecs);
    const { level: alignLevel, label: alignLabel, rationale: alignRationale } = getStrategicAlignment(wardContext?.artemis_strategy?.tier, results?.input_summary?.campaign_focus);
    const { level: potentialLevel, rationale: potentialRationale, percent: potentialPercent } = getUntappedPotential(tacticalIntelligence);

    // Fix: Fatigue Logic - Read from correct engine path (sophisticated_research_metrics.power_law_fatigue_ratio)
    const fatigueRatios = (wardAnalysis?.ranked_channels ?? []).map(c => 
        wardAnalysis?.channel_performance?.[c.channel]?.sophisticated_research_metrics?.power_law_fatigue_ratio
    ).filter(m => m !== undefined);
    
    const avgFatigueRatio = fatigueRatios.length > 0 ? fatigueRatios.reduce((a, b) => a + b, 0) / fatigueRatios.length : 1.0;
    const fatiguePercent = (1 - avgFatigueRatio) * 100;

    const handleCreateReport = () => {
        alert("Report generation is available in the main console.");
    };

    const handleConfirmDelete = () => {
        if (onDelete) onDelete();
        setShowDeleteModal(false);
    };

    // --- STRATEGIC DATA PREPARATION & DEFAULT TAB SETTING ---
    const expansionRecs = expansionOps?.recommendations ?? [];
    const stratCanvass = expansionRecs.find(r => r.channel === 'strategic_canvassing');
    const surplusFinancialRecs = expansionRecs.filter(r => r.channel !== 'strategic_canvassing'); 
    
    const hasSurplus = expansionOps?.status === 'OPPORTUNITY_DETECTED';
    const hasCanvass = !!stratCanvass;
    const hasResonance = !!currentWardResonance;

    // Effect: Set initial active tab based on availability
    useEffect(() => {
        if (!activeStrategicTab) {
            if (hasSurplus) setActiveStrategicTab('surplus');
            else if (hasCanvass) setActiveStrategicTab('canvassing');
            else if (hasResonance) setActiveStrategicTab('resonance');
        }
    }, [hasSurplus, hasCanvass, hasResonance, activeStrategicTab]);

    // Helper to get tab styling
    const getTabTheme = () => {
        if (activeStrategicTab === 'surplus') return { border: 'border-emerald-500/30', bg: 'bg-gradient-to-br from-emerald-900/20 to-slate-900/50', accent: 'emerald' };
        if (activeStrategicTab === 'canvassing') return { border: 'border-blue-500/30', bg: 'bg-blue-900/10', accent: 'blue' };
        if (activeStrategicTab === 'resonance') return { border: 'border-indigo-500/20', bg: 'bg-slate-900/30', accent: 'indigo' };
        return { border: 'border-slate-700', bg: 'bg-slate-800', accent: 'slate' };
    };

    const tabTheme = getTabTheme();

    if (!results) {
        return (<div className="p-8 text-center text-slate-400"><p>No results data available. Please run an analysis.</p></div>);
    }
    
    const modelScores = [
        { label: "Resource Efficiency", score: performance?.campaign_scorecard?.component_scores?.resource_efficiency ?? 0, color: "bg-gradient-to-r from-emerald-400 to-teal-500" },
        { label: "Channel Diversity", score: performance?.campaign_scorecard?.channel_diversity_score ?? 85, color: "bg-gradient-to-r from-blue-400 to-indigo-500" }, 
        { label: "Expected ROI Score", score: performance?.campaign_scorecard?.expected_roi_score ?? 78, color: "bg-gradient-to-r from-cyan-400 to-blue-500" },
        { label: "Predictability", score: performance?.campaign_scorecard?.predictability_score, color: "bg-gradient-to-r from-purple-400 to-fuchsia-500" },
        { label: "Budget Utilization", score: (performance?.efficiency_metrics?.budget_utilization_rate ?? 0.95) * 100, color: "bg-gradient-to-r from-amber-400 to-orange-500" }
    ];

    // --- Calculated Metrics for Summary Box ---
    const allocations = results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? [];
    const topAllocation = [...allocations].sort((a,b) => b.allocation_percentage - a.allocation_percentage)[0];
    
    // --- New Field Extraction ---
    const projectedGrossVotes = results?.campaign_optimization?.optimal_budget_allocation?.projected_gross_votes;
    
    // Extract unique Rationale Codes for "Constraint Analysis"
    const constraintCodes = [...new Set(allocations.map(a => a.rationale_code).filter(Boolean))];
    
    // Calculate Weighted Average Fit
    const weightedFit = allocations.length > 0 ? allocations.reduce((acc, curr) => {
        const recData = allRecs.find(r => r.channel === curr.channel);
        const fit = (recData?.average_demographic_fit ?? 0) * 100;
        return acc + (fit * (curr.allocation_percentage / 100));
    }, 0) : 0;
    
    // --- Hybrid ROI Check ---
    const isHybridROI = campaignMetrics?.gross_votes_per_hour > 0 && campaignMetrics?.gross_cost_per_vote > 0;
    const isGrossVotesSplit = projectedGrossVotes !== undefined && projectedGrossVotes !== null;

    // --- SORTING LOGIC FOR CHANNEL ANALYSIS ---
    // Sort channels by demographic_fit descending (High to Low)
    const sortedChannels = wardAnalysis?.ranked_channels 
        ? [...wardAnalysis.ranked_channels].sort((a, b) => (b.demographic_fit ?? 0) - (a.demographic_fit ?? 0))
        : [];

    return (
        <div className="space-y-8 bg-slate-950 p-6 min-h-screen relative">
            {/* Header */}
            <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-start">
                    <div className="flex flex-col items-start text-left">
                        <h3 className="text-2xl font-bold text-white mb-2">
                            Simulated Campaign Results
                        </h3>
                        
                        <p className="text-slate-400 mb-2">Analysis for campaign: '{results.analysis_metadata?.campaign_name ?? 'Untitled Campaign'}'</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                            <span>Focus: <span className="font-medium text-slate-300">{(results?.input_summary?.campaign_focus ?? 'N/A').replace(/_/g, ' ')}</span></span>
                            <span>Budget: <span className="font-medium text-slate-300">£{(results?.input_summary?.budget ?? 0).toLocaleString()}</span></span>
                            {/* UPDATED: Show Hours if allocated */}
                            {results?.campaign_optimization?.optimal_budget_allocation?.total_hours_allocated > 0 && (
                                <span>Vol. Hours: <span className="font-medium text-blue-300">{(results.campaign_optimization.optimal_budget_allocation.total_hours_allocated).toFixed(0)}</span></span>
                            )}
                            <span className="text-slate-500">|</span>
                            <span>Wards Analyzed: <span className="font-medium text-white">{wardKeys.length}</span></span>
                        </div>
                    </div>
                    
                    <div className="flex flex-col items-end">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition-all shadow-lg group mb-4"
                            >
                                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300"/>
                                <span className="text-xs font-bold uppercase tracking-wider">Back to Library</span>
                            </button>
                        )}
                        <div className="text-right text-xs text-slate-500 hidden md:block">
                            <p>PLATO v{results?.analysis_metadata?.plato_version ?? 'N/A'}</p>
                            <p>Session: {results?.analysis_metadata?.session_id ?? 'N/A'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- INPUT SUMMARY HUD STRIP --- */}
            <div className="w-full mb-6">
                <h6 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 ml-1">Input Summary</h6>
                
                <div className="relative w-full bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-xl overflow-hidden flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-slate-800/50">
                    
                    {/* Resource Group */}
                    <div className="flex-1 flex divide-x divide-slate-800/50">
                        <div className="flex-1 p-4 flex flex-col justify-center hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                <Wallet size={12} className="text-emerald-500"/> Budget
                            </div>
                            <div className="text-lg font-mono font-medium text-white">
                                £{(campaignInput?.budget ?? 0).toLocaleString()}
                            </div>
                        </div>

                        <div className="flex-1 p-4 flex flex-col justify-center hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                <Users size={12} className="text-blue-500"/> Vol. Cap
                            </div>
                            <div className="text-lg font-mono font-medium text-blue-200">
                                {(campaignInput?.total_volunteer_hours ?? 0).toLocaleString()} <span className="text-xs text-slate-600">hrs</span>
                            </div>
                        </div>
                    </div>

                    {/* Strategy Group */}
                    <div className="flex-[1.5] p-4 flex flex-col justify-center hover:bg-white/5 transition-colors">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                            <Crosshair size={12} className="text-purple-500"/> Strategy
                        </div>
                        <div className="text-sm font-medium text-white truncate" title={campaignInput?.campaign_focus}>
                            {formatChannelName(campaignInput?.campaign_focus)}
                        </div>
                    </div>

                    {/* Scope & Time Group */}
                    <div className="flex-1 flex divide-x divide-slate-800/50">
                        <div className="flex-1 p-4 flex flex-col justify-center hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                <MapPin size={12} className="text-slate-400"/> Scope
                            </div>
                            <div className="text-sm font-medium text-slate-300">
                                {campaignInput?.ward_count ?? 0} <span className="text-xs text-slate-500">Wards</span>
                            </div>
                        </div>

                        <div className="flex-1 p-4 flex flex-col justify-center hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                <Timer size={12} className="text-orange-500"/> Election
                            </div>
                            <div className="text-sm font-medium text-slate-200">
                                {campaignInput?.days_until_election ?? 0} <span className="text-xs text-slate-500">Days</span>
                            </div>
                        </div>
                    </div>

                    {/* Execution Meta */}
                    <div className="flex-[1.5] p-4 flex flex-col justify-center bg-slate-900/80 hover:bg-white/5 transition-colors">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                <Calendar size={12} className="text-slate-400"/> Window
                            </div>
                            {campaignInput?.is_pulse_campaign && (
                                <Activity size={10} className="text-indigo-400 animate-pulse"/>
                            )}
                        </div>
                        <div className="text-xs font-mono text-slate-400 mb-0.5 whitespace-nowrap">
                            {formatFriendlyDate(campaignInput?.debug_start_date)} <span className="text-slate-600">→</span> {formatFriendlyDate(campaignInput?.debug_end_date)}
                        </div>
                        <div className="text-[10px] text-slate-600">
                            {campaignInput?.execution_window_days ?? 0} days active
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <GradeBadge grade={performance?.campaign_scorecard?.overall_grade ?? 'N/A'} />
                
                <div className="kpi-card bg-slate-800/50 p-4 rounded-xl border border-white/5">
                    <div className="kpi-title text-slate-500 font-bold text-xs uppercase tracking-widest mb-2">Top Channel</div>
                    <div className="kpi-value text-accent-400 text-2xl font-bold font-mono">{formatChannelName(campaignMetrics?.best_channel)}</div>
                    {/* UPDATED: Contextual label for Hybrid/Time */}
                    <div className="text-xs text-slate-400 mt-1">
                        {campaignMetrics?.gross_votes_per_hour > 0 ? "Highest Multi-Resource Efficiency" : "Highest Financial ROI"}
                    </div>
                </div>
                
                {/* UPDATED: Projected Votes (Supports Gross Split) */}
                <div className={`kpi-card bg-slate-800/50 rounded-xl border border-white/5 ${isGrossVotesSplit ? 'overflow-hidden flex flex-col justify-between' : 'p-4'}`}>
                    {isGrossVotesSplit ? (
                        <>
                             <div className="flex-1 px-5 py-2.5 border-b border-slate-700/50 flex flex-col justify-center bg-slate-800/20">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                    <span>Total Outcome (Gross)</span>
                                    <Users size={10}/>
                                </div>
                                <div className="text-xl font-bold text-white font-mono">{Math.round(projectedGrossVotes ?? 0).toLocaleString()} Votes</div>
                            </div>
                            <div className="flex-1 px-5 py-2.5 bg-slate-800/40 flex flex-col justify-center">
    <div className="text-[10px] uppercase tracking-wider text-accent-300 flex items-center justify-between">
        <span>Projected Lift</span>
        <TrendingUp size={10}/>
    </div>
    <div className="text-xl font-bold text-accent-400 font-mono">
        +{Math.round(results?.campaign_optimization?.optimal_budget_allocation?.expected_total_votes ?? 0).toLocaleString()} Votes
    </div>
</div>
                        </>
                    ) : (
                        <>
                            <div className="kpi-title text-slate-500 font-bold text-xs uppercase tracking-widest mb-2">Projected Votes (Mean)</div>
                            <div className="kpi-value text-3xl font-bold text-white font-mono">{(monteCarloProjections?.mean ?? 0).toFixed(0)}</div>
                            <div className="text-xs text-slate-400 mt-1">Range: {(monteCarloProjections?.['5th_percentile'] ?? 0).toFixed(0)} - {(monteCarloProjections?.['95th_percentile'] ?? 0).toFixed(0)} (95% CI)</div>
                        </>
                    )}
                </div>
                
                {/* UPDATED: Hybrid ROI Split Logic */}
                <div className={`kpi-card bg-slate-800/50 rounded-xl border border-white/5 ${isHybridROI ? 'overflow-hidden flex flex-col justify-between' : 'p-4'}`}>
                    {isHybridROI ? (
                        <>
                            <div className="flex-1 px-5 py-2.5 border-b border-slate-700/50 flex flex-col justify-center bg-slate-800/20">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                    <span>Financial</span>
                                    <DollarSign size={10}/>
                                </div>
                                <div className="text-xl font-bold text-white font-mono">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)} / Vote</div>
                            </div>
                            <div className="flex-1 px-5 py-2.5 bg-slate-800/40 flex flex-col justify-center">
                                <div className="text-[10px] uppercase tracking-wider text-blue-300 flex items-center justify-between">
                                    <span>Time</span>
                                    <Clock size={10}/>
                                </div>
                                <div className="text-xl font-bold text-blue-400 font-mono">{(campaignMetrics?.gross_votes_per_hour ?? 0).toFixed(1)} Votes / Hr</div>
                            </div>
                        </>
                    ) : (
                        // Standard Single ROI View (Financial OR Time Only)
                        <>
                             {campaignMetrics?.gross_votes_per_hour > 0 && campaignMetrics?.gross_cost_per_vote === 0 ? (
                                <>
                                    <div className="kpi-title text-slate-500 font-bold text-xs uppercase tracking-widest mb-2">Votes Per Hour</div>
                                    <div className="kpi-value text-blue-400 text-3xl font-bold font-mono">{(campaignMetrics.gross_votes_per_hour).toFixed(1)}</div>
                                    <div className="text-xs text-slate-400 mt-1">Volunteer Efficiency</div>
                                </>
                            ) : (
                                <>
                                    <div className="kpi-title text-slate-500 font-bold text-xs uppercase tracking-widest mb-2">Eff. Cost / Vote</div>
                                    <div className="kpi-value text-3xl font-bold text-white font-mono">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)}</div>
                                    <div className="text-xs text-slate-400 mt-1">Financial Efficiency</div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
            
            {/* Performance & Risk (UPDATED WITH MODERN BARS) */}
            <div className="panel p-6 bg-slate-900/40 border border-slate-800 rounded-xl">
                <h5 className="text-base font-semibold text-white mb-4">Performance & Risk</h5>
                {/* ScoreBars updated to ModernProgressBar */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
                    {modelScores.map(item => (<ModernProgressBar key={item.label} label={item.label} score={item.score} colorClass={item.color || "bg-accent-500"} />))}
                </div>
                <div className="border-t border-slate-700 mt-6 pt-4">
                     <h6 className="text-sm font-semibold text-white mb-3">Risk Analysis</h6>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <WardStat icon={<AlertTriangle size={20} className="text-yellow-400"/>} label="Volatility Index" value={`${((monteCarloRisks?.volatility_index ?? 0) * 100).toFixed(1)}%`} />
                        <WardStat icon={<TrendingUp size={20} className="text-success-400"/>} label="Upside Potential (95th %)" value={`${(monteCarloProjections?.['95th_percentile'] ?? 0).toFixed(0)} votes`} />
                        <WardStat icon={<TrendingDown size={20} className="text-danger-400"/>} label="Downside Risk (5th %)" value={`${(monteCarloProjections?.['5th_percentile'] ?? 0).toFixed(0)} votes`} />
                     </div>
                </div>

                {/* UPDATED: Constraint Logic / Rationale Codes (Plain Text) */}
                {constraintCodes.length > 0 && (
                    <div className="border-t border-slate-700 mt-6 pt-4">
                        <h6 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                             <Scale size={16} className="text-slate-400"/> Constraint Logic Analysis
                        </h6>
                        <p className="text-xs text-slate-400">
                            Primary optimization constraints: <span className="text-slate-200">{constraintCodes.map(code => code.replace(/_/g, ' ')).join(', ')}</span>
                        </p>
                    </div>
                )}
            </div>

            {/* --- Parent Grid --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
                {/* --- START: Left/Main Column --- */}
                <div className="lg:col-span-2 space-y-8">

                   {/* DYNAMIC CAMPAIGN MODE INDICATOR (REALITY-FIRST LOGIC) */}
                {(() => {
                    // 1. Safe Access to Critical Data
                    const timeBasedRec = results?.campaign_optimization?.budget_expansion_opportunities?.recommendations?.find(
                        r => r.channel === 'strategic_canvassing' || r.channel === 'pulse_war_plan'
                    );

                    // Only render if we have a time-based strategy to show
                    if (!timeBasedRec) return null;

                    // 2. Data Extraction
                    const windowDays = results?.input_summary?.execution_window_days || 0;
                    const gapDays = timeBasedRec.gap_days || 0;
                    const channelName = (timeBasedRec.channel || '').toLowerCase();
                    
                    // --- CRITICAL FIX: READ REALITY (ALLOCATION), NOT THEORY (RECOMMENDATION) ---
                    const realAllocatedHours = results?.campaign_optimization?.optimal_budget_allocation?.total_hours_allocated || 0;

                    // 3. Schedule Analysis
                    const rawSchedule = timeBasedRec.weekly_schedule || {};
                    const scheduleKeys = Object.keys(rawSchedule);
                    const isWeekBased = scheduleKeys.some(k => k.toLowerCase().includes('week'));

                    // 4. INTELLIGENT MODE DETECTION (Duration-First Hierarchy)
                    let currentMode = 'MARATHON'; // Default fallback

                    if (windowDays <= 1) {
                        currentMode = 'ACTION_DAY'; // 1 Day
                    } else if (windowDays <= 3) {
                        currentMode = 'BLITZ'; // 2-3 Days (Weekend)
                    } else if (gapDays > 14 && windowDays > 3) { 
                        currentMode = 'FLIGHT'; // Stops early (Gap)
                    } else if (windowDays <= 21) {
                        currentMode = 'PULSE'; // 4-21 Days
                    }

                    // 5. Configuration Dictionary
                    const modeConfigs = {
                        'ACTION_DAY': {
                            theme: "rose",
                            bgGradient: "from-rose-900/40 to-slate-900/80",
                            borderColor: "border-rose-500/50",
                            iconBg: "bg-rose-500/20",
                            iconColor: "text-rose-400",
                            iconBorder: "border-rose-500/30",
                            MainIcon: Megaphone, 
                            title: "Action Day Active",
                            tagBg: "bg-rose-500",
                            tagText: "text-white",
                            desc: "Single-day high-intensity mobilization. All resources concentrated into one operational window.",
                            glow: "bg-rose-500/10",
                            velocityColor: "text-rose-100"
                        },
                        'BLITZ': {
                            theme: "rose",
                            bgGradient: "from-rose-900/30 to-slate-900/70",
                            borderColor: "border-rose-500/40",
                            iconBg: "bg-rose-500/20",
                            iconColor: "text-rose-400",
                            iconBorder: "border-rose-500/30",
                            MainIcon: Zap, 
                            title: "Weekend Blitz",
                            tagBg: "bg-rose-500",
                            tagText: "text-white",
                            desc: "48-72 hour concentrated surge. Optimization prioritizes maximum contact density over this specific weekend.",
                            glow: "bg-rose-500/10",
                            velocityColor: "text-rose-100"
                        },
                        'FLIGHT': {
                            theme: "indigo",
                            bgGradient: "from-indigo-900/40 to-slate-900/80",
                            borderColor: "border-indigo-500/40",
                            iconBg: "bg-indigo-500/20",
                            iconColor: "text-indigo-400",
                            iconBorder: "border-indigo-500/20",
                            MainIcon: Mail, 
                            title: "Flight Window Active",
                            tagBg: "bg-indigo-500",
                            tagText: "text-white",
                            desc: `Campaign operates for ${windowDays} days before stopping ${gapDays} days out. Strategy prioritizes early saturation.`,
                            glow: "bg-indigo-500/10",
                            velocityColor: "text-indigo-100"
                        },
                        'PULSE': {
                            theme: "amber",
                            bgGradient: "from-amber-900/20 to-slate-900/60",
                            borderColor: "border-amber-500/30",
                            iconBg: "bg-amber-500/20",
                            iconColor: "text-amber-400",
                            iconBorder: "border-amber-500/20",
                            MainIcon: Zap,
                            title: "Tactical Pulse",
                            tagBg: "bg-amber-500",
                            tagText: "text-slate-900",
                            desc: "Momentum calculations accelerated. Optimization is strictly bound to this high-intensity window.",
                            glow: "bg-amber-500/5",
                            velocityColor: "text-amber-100"
                        },
                        'MARATHON': {
                            theme: "emerald",
                            bgGradient: "from-emerald-900/20 to-slate-900/60",
                            borderColor: "border-emerald-500/30",
                            iconBg: "bg-emerald-500/20",
                            iconColor: "text-emerald-400",
                            iconBorder: "border-emerald-500/20",
                            MainIcon: Activity,
                            title: "Strategic Marathon",
                            tagBg: "bg-emerald-500",
                            tagText: "text-slate-900",
                            desc: `Sustained pressure over ${Math.ceil(windowDays / 7)} weeks. Focuses on preventing volunteer burnout and maximizing retention.`,
                            glow: "bg-emerald-500/5",
                            velocityColor: "text-emerald-100"
                        }
                    };

                    const config = modeConfigs[currentMode];

                    // 6. Content Reactivity
                    let displayRationale = timeBasedRec.rationale || timeBasedRec.timing_rationale || "Maximize frequency within the window.";
                    
                    // Override rationale for Postal/Leaflet channels
                    if (channelName.includes('postal') || channelName.includes('leaflet') || channelName.includes('mail')) {
                        displayRationale = "Strategy: Recurring drops scheduled to counter decay. High-frequency physical touchpoints required to anchor memory.";
                    }

                    // 7. Timeline Logic (Hide timeline for Short Campaigns)
                    const showTimeline = !['ACTION_DAY', 'BLITZ'].includes(currentMode);

                    const sortedKeys = showTimeline ? scheduleKeys.sort((a, b) => {
                        if (isWeekBased) {
                            const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
                            const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
                            return numB - numA; 
                        } else {
                            const order = ['friday', 'saturday', 'sunday', 'monday', 'election'];
                            const getIndex = (str) => order.findIndex(day => str.toLowerCase().includes(day));
                            const idxA = getIndex(a);
                            const idxB = getIndex(b);
                            return (idxA !== -1 && idxB !== -1) ? idxA - idxB : a.localeCompare(b);
                        }
                    }) : [];

                    // 8. Active Data Resolution
                    let displayWindow, displayVols, displayHours, windowLabel;

                    if (!showTimeline) {
                        // --- AGGREGATE MODE (For Blitz/Action Day) ---
                        displayWindow = timeBasedRec.recommended_time_window;
                        displayVols = timeBasedRec.min_volunteers_required;
                        
                        // FIX: Prefer Real Allocation (85) over Theoretical Sum (164)
                        displayHours = realAllocatedHours > 0 ? realAllocatedHours : Object.values(rawSchedule).reduce((acc, curr) => {
                            const h = typeof curr === 'object' ? (curr.hours || 0) : curr;
                            return acc + h;
                        }, 0);

                        windowLabel = "Operational Window";
                        
                    } else {
                        // --- TIMELINE MODE (For Marathon/Pulse) ---
                        const activeData = (selectedPulseDay && rawSchedule[selectedPulseDay]) 
                            ? rawSchedule[selectedPulseDay] 
                            : null;

                        displayWindow = activeData?.window || timeBasedRec.recommended_time_window;
                        displayVols = activeData?.volunteers_needed || timeBasedRec.min_volunteers_required;
                        
                        // In timeline mode, we show the slice hours if selected, otherwise the total real allocation
                        displayHours = activeData?.hours || (realAllocatedHours > 0 ? realAllocatedHours : 0);
                        
                        windowLabel = selectedPulseDay ? (isWeekBased ? 'Weekly Shift' : 'Daily Shift') : 'Global Window';
                    }

                return (
                        <div className={`p-5 rounded-xl border ${config.borderColor} bg-gradient-to-r ${config.bgGradient} relative overflow-hidden group transition-all duration-500`}>
            
            {/* Ambient Background Effects */}
            <div className={`absolute top-0 right-0 w-64 h-64 ${config.glow} rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none`}></div>
            <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-${config.theme}-500/50 to-transparent opacity-30`}></div>

            <div className="relative z-10 space-y-5">
                {/* TOP ROW: Header & Context */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={`p-2.5 ${config.iconBg} rounded-lg ${config.iconColor} border ${config.iconBorder} shadow-lg backdrop-blur-sm`}>
                            <config.MainIcon size={22} />
                        </div>
                        <div>
                            <h4 className="text-lg font-bold text-white flex items-center gap-3">
                                {config.title}
                                <span className={`text-[10px] uppercase ${config.tagBg} ${config.tagText} px-2 py-0.5 rounded-md font-black tracking-wider shadow-sm`}>
                                    {windowDays} Day Window
                                </span>
                            </h4>
                            <p className="text-xs text-slate-300 mt-1.5 leading-relaxed max-w-lg">
                                {config.desc}
                            </p>
                            
                            {/* Date & Context */}
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
                                <div className={`flex items-center gap-2 text-xs ${config.theme === 'rose' ? 'text-rose-200/80' : config.theme === 'amber' ? 'text-amber-200/80' : 'text-emerald-200/80'}`}>
                                    <Calendar size={14} className={config.iconColor}/>
                                    <span className={`font-mono font-medium ${config.velocityColor}`}>
                                    {(() => {
                                        const formatDate = (dateStr) => {
                                            if (!dateStr) return 'N/A';
                                            return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                                        };
                                        // FIX: Use results.input_summary which is already available in your scope
                                        return `${formatDate(results?.input_summary?.execution_start_date)} → ${formatDate(results?.input_summary?.execution_end_date)}`;
                                    })()}
                                </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                    <Timer size={14} />
                                    <span>Intensity: <span className="text-white font-bold">{currentMode === 'ACTION_DAY' ? 'Max' : currentMode === 'BLITZ' ? 'Very High' : 'Standard'}</span></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visual Graph Animation (Color Adaptive) */}
                    <div className="hidden sm:flex items-end gap-1 h-12 opacity-60">
                        <div className={`w-1.5 bg-${config.theme}-500/20 h-4 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/30 h-6 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/50 h-3 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/80 h-8 rounded-full animate-pulse`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500 h-10 rounded-full shadow-[0_0_10px_rgba(var(--${config.theme}-500),0.5)]`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/80 h-5 rounded-full`}></div>
                        <div className={`w-1.5 bg-${config.theme}-500/40 h-3 rounded-full`}></div>
                    </div>
                </div>

                {/* MIDDLE ROW: Operational Strategy Text */}
                <div className={`border-t ${config.borderColor} pt-4 mt-2 animate-in fade-in slide-in-from-bottom-2 duration-500`}>
                    <div className="mb-5">
                        <h6 className={`text-[10px] font-bold ${config.iconColor} uppercase tracking-wider flex items-center gap-1.5 mb-2`}>
                            <Activity size={12}/> Operational Strategy
                        </h6>
                        <p className={`text-sm text-slate-200 italic leading-relaxed pl-1 border-l-2 ${config.borderColor}`}>
                            "{displayRationale}"
                        </p>
                    </div>

                    {/* BOTTOM ROW: Interactive Timeline (HIDDEN IF BLITZ/ACTION DAY) */}
                    {showTimeline && sortedKeys.length > 0 && (
                        <div className="mb-5">
                            <div className="flex space-x-2 overflow-x-auto pb-2 custom-scrollbar mask-linear-fade">
                                {sortedKeys.map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedPulseDay(selectedPulseDay === key ? null : key)}
                                        className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
                                            selectedPulseDay === key 
                                            ? `bg-${config.theme}-500 text-white border-${config.theme}-400 font-bold shadow-md` 
                                            : `bg-${config.theme}-900/20 text-slate-400 border-${config.theme}-500/10 hover:bg-${config.theme}-900/40 hover:text-white hover:border-${config.theme}-500/30`
                                        }`}
                                    >
                                        {key.includes('Week') 
                                            ? key.replace('Week', 'W') 
                                            : key.split('(')[0].replace('Action Day', '').trim() || key.split('(')[0].trim()
                                        }
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Metrics Dashboard */}
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-1 min-h-[40px]">
                        {/* Metric 1: Window */}
                        {displayWindow && (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`win-${selectedPulseDay || 'global'}`}>
                                <Clock size={16} className={config.iconColor}/>
                                <div className="flex flex-col">
                                    <span className={`text-[9px] text-${config.theme}-500/60 uppercase font-bold tracking-wider`}>
                                        {windowLabel}
                                    </span>
                                    <span className="text-sm font-bold text-white leading-none">{displayWindow}</span>
                                </div>
                            </div>
                        )}

                        {/* Metric 2: Hours */}
                        {(displayHours !== undefined && displayHours > 0) ? (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`hrs-${selectedPulseDay || 'global'}`}>
                                <Timer size={16} className="text-orange-400"/>
                                <div className="flex flex-col">
                                    <span className="text-[9px] text-orange-500/60 uppercase font-bold tracking-wider">Target Hours</span>
                                    <span className="text-sm font-bold text-white leading-none font-mono">{displayHours.toFixed(1)} Hrs</span>
                                </div>
                            </div>
                        ) : (
                            timeBasedRec.gap_days !== undefined && !['ACTION_DAY', 'BLITZ'].includes(currentMode) && (
                                <div className="flex items-center gap-2 animate-in fade-in duration-300" key="gap-global">
                                    <Calendar size={16} className="text-slate-400"/>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] text-slate-500 uppercase font-bold tracking-wider">Campaign Gap</span>
                                        <span className="text-sm font-bold text-white leading-none font-mono">{timeBasedRec.gap_days} Days</span>
                                    </div>
                                </div>
                            )
                        )}

                        {/* Metric 3: Volunteers */}
                        {displayVols !== undefined && (
                            <div className="flex items-center gap-2 animate-in fade-in duration-300" key={`vol-${selectedPulseDay || 'global'}`}>
                                <Users size={16} className={`text-${config.theme}-400`}/>
                                <div className="flex flex-col">
                                    <span className={`text-[9px] text-${config.theme}-500/60 uppercase font-bold tracking-wider`}>
                                        {selectedPulseDay ? 'Vols Needed' : 'Peak Vols'}
                                    </span>
                                    <span className="text-sm font-bold text-white leading-none font-mono">{displayVols} Active</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
})()}
                    {/* Budget Allocation (Hybrid Aware + Collapsible + Modern Bars) */}
                    <div className="panel p-6 bg-slate-900/40 border border-slate-800 rounded-xl">
                        <div className="flex justify-between items-center mb-4">
                             <h4 className="text-xl font-semibold text-white">Optimal Resource Allocation</h4>
                             <button onClick={() => setIsAllocationsExpanded(!isAllocationsExpanded)} className="text-slate-400 hover:text-white transition-colors">
                                 {isAllocationsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                             </button>
                        </div>
                        
                        {isAllocationsExpanded ? (
                            <div className="space-y-4">
                                {(results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? []).map(alloc => {
                                    const recData = allRecs.find(r => r.channel === alloc.channel);
                                    const isTimeOnly = alloc.resource_mode === 'time_only';
                                    const isHybrid = alloc.resource_mode === 'hybrid';
                                    
                                    // Calculate Efficiency Bar - Updated to use realized CPV & Fix for Division by Zero
                                    const realizedCPV = alloc.expected_votes > 0 && alloc.allocated_budget > 0 
                                        ? alloc.allocated_budget / alloc.expected_votes 
                                        : (recData?.average_cost_per_vote ?? maxCost);

                                    let costEfficiencyPct = 0;
                                    
                                    // FIXED LOGIC for Resource Efficiency Bar
                                    if (!isTimeOnly && realizedCPV !== Infinity) {
                                        if (costRange > 0) {
                                            costEfficiencyPct = ((maxCost - realizedCPV) / costRange) * 100;
                                        } else {
                                            // Fallback: If costRange is 0 (only 1 channel or all equal), assume high efficiency if selected
                                            costEfficiencyPct = 100;
                                        }
                                        costEfficiencyPct = Math.max(10, Math.min(100, costEfficiencyPct));
                                    } else if (isTimeOnly) {
                                        costEfficiencyPct = 95; 
                                    }
                                    
                                    const demoFitPct = (recData?.average_demographic_fit ?? (wardAnalysis?.ranked_channels.find(c => c.channel === alloc.channel)?.demographic_fit ?? 0)) * 100;
                                    
                                    return (
                                    <div key={alloc.channel} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 relative overflow-hidden">
                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${isTimeOnly ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-emerald-500'}`}></div>
                                        
                                        <div className="flex justify-between items-center mb-1 text-sm pl-2">
                                            <span className="font-medium text-slate-200 flex items-center gap-2">
                                                {formatChannelName(alloc.channel)}
                                                {isTimeOnly && <Clock size={14} className="text-blue-400"/>}
                                                {isHybrid && <Layers size={14} className="text-purple-400"/>}
                                            </span>
                                            <span className="font-mono text-slate-300">
                                                {formatResourceValue(alloc.allocated_budget, alloc.allocated_hours, alloc.resource_mode)}
                                                <span className="text-slate-500 ml-2">({(alloc.expected_votes ?? 0).toFixed(0)} votes)</span>
                                            </span>
                                        </div>
                                        
                                        <div className="w-full bg-slate-700 rounded-full h-2.5 mb-3 ml-2 pr-2">
                                            <div className={`${isTimeOnly ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-accent-500'} h-2.5 rounded-full`} 
                                                 style={{ width: `${alloc.allocation_percentage ?? 0}%` }} 
                                                 title={`Allocation: ${alloc.allocation_percentage?.toFixed(1)}%`}>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4 pl-2">
                                            <ModernProgressBar label={isTimeOnly ? "Time Efficiency" : "Resource Efficiency"} score={costEfficiencyPct} colorClass={isTimeOnly ? "bg-gradient-to-r from-blue-400 to-cyan-500" : "bg-gradient-to-r from-emerald-400 to-teal-500"} />
                                            <ModernProgressBar label="Demographic Fit" score={demoFitPct} colorClass="bg-gradient-to-r from-indigo-500 to-purple-500" />
                                        </div>
                                    </div>
                                )})}
                                {(results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? []).length === 0 && (
                                    <p className="text-slate-400 italic">No allocations generated. Check constraints.</p>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                                    <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Top Allocation</span>
                                    <div className="text-lg font-bold text-white truncate" title={topAllocation?.channel}>{formatChannelName(topAllocation?.channel || 'None')}</div>
                                    <div className="text-sm text-accent-400 font-mono">{(topAllocation?.allocation_percentage ?? 0).toFixed(1)}% Share</div>
                                </div>
                                <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                                    <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Global Efficiency</span>
                                    {isHybridROI ? (
                                        <div className="text-xs text-white">Hybrid Optimized</div>
                                    ) : campaignMetrics?.gross_votes_per_hour > 0 ? (
                                         <div className="text-lg font-bold text-blue-400 font-mono">{campaignMetrics.gross_votes_per_hour.toFixed(1)} VPH</div>
                                    ) : (
                                        <div className="text-lg font-bold text-emerald-400 font-mono">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)} CPV</div>
                                    )}
                                    <div className="text-xs text-slate-500">Aggregate Performance</div>
                                </div>
                                <div className="bg-slate-700/30 p-4 rounded border border-slate-600/50 flex flex-col justify-center">
                                    <span className="text-xs uppercase text-slate-400 font-semibold mb-1">Weighted Fit</span>
                                    <div className="text-lg font-bold text-indigo-400 font-mono">{weightedFit.toFixed(0)}%</div>
                                    <ModernProgressBar score={weightedFit} colorClass="bg-gradient-to-r from-indigo-500 to-purple-500" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* --- STRATEGIC OVERVIEW TABBED CONTAINER (REFACTORED) --- */}
                    {(hasSurplus || hasCanvass || hasResonance) && (
                        <div className={`panel p-6 bg-slate-900/40 rounded-xl ${tabTheme.bg} border ${tabTheme.border} relative overflow-hidden transition-all duration-300`}>
                            
                            {/* TAB HEADERS (KPI STYLE) */}
                            <div className="grid grid-cols-3 gap-4 mb-6 relative z-10">
                                {hasSurplus && (
                                    <button 
                                        onClick={() => setActiveStrategicTab('surplus')}
                                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'surplus' ? 'bg-emerald-500/20 border-emerald-500/50 shadow-[0_0_15px_-3px_rgba(16,185,129,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'surplus' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-400'}`}>
                                                <Sparkles size={16} />
                                            </div>
                                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'surplus' ? 'text-emerald-300' : 'text-slate-400'}`}>Capital</span>
                                        </div>
                                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'surplus' ? 'text-white' : 'text-slate-300'}`}>Surplus Deployment</div>
                                    </button>
                                )}

                                {hasCanvass && (
                                    <button 
                                        onClick={() => setActiveStrategicTab('canvassing')}
                                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'canvassing' ? 'bg-blue-500/20 border-blue-500/50 shadow-[0_0_15px_-3px_rgba(59,130,246,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'canvassing' ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                                                <Users size={16} />
                                            </div>
                                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'canvassing' ? 'text-blue-300' : 'text-slate-400'}`}>Tactical</span>
                                        </div>
                                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'canvassing' ? 'text-white' : 'text-slate-300'}`}>Strategic Canvassing</div>
                                    </button>
                                )}

                                {hasResonance && (
                                    <button 
                                        onClick={() => setActiveStrategicTab('resonance')}
                                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'resonance' ? 'bg-indigo-500/20 border-indigo-500/50 shadow-[0_0_15px_-3px_rgba(99,102,241,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'resonance' ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                                                <Brain size={16} />
                                            </div>
                                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'resonance' ? 'text-indigo-300' : 'text-slate-400'}`}>Overlay</span>
                                        </div>
                                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'resonance' ? 'text-white' : 'text-slate-300'}`}>Resonance Strategy</div>
                                    </button>
                                )}
                            </div>

                            {/* TAB CONTENT */}
                            <div className="relative z-10 min-h-[300px]">
                                {/* 1. SURPLUS CAPITAL DEPLOYMENT */}
                                {activeStrategicTab === 'surplus' && hasSurplus && (
                                    <div className="animate-in fade-in zoom-in-95 duration-300">
                                        <div className="flex items-center gap-2 mb-6 p-3 bg-slate-800/60 rounded border border-slate-700/50">
                                            <div className="bg-slate-700 p-1.5 rounded text-slate-400"><Wallet size={14}/></div>
                                            <div className="text-xs text-slate-300">
                                                Available Surplus: <span className="text-white font-mono font-bold">£{(expansionOps.surplus_available ?? 0).toLocaleString()}</span>
                                            </div>
                                            <div className="ml-auto text-[10px] font-bold bg-emerald-500 text-slate-900 px-2 py-0.5 rounded-full uppercase tracking-wider">Tactical Financial</div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {surplusFinancialRecs.map((rec, idx) => (
                                                <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 hover:border-emerald-500/40 transition-colors group">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h5 className="font-bold text-white flex items-center gap-2">
                                                            {formatChannelName(rec.channel)}
                                                            <ArrowRight size={14} className="text-slate-500 group-hover:text-emerald-400 transition-colors"/>
                                                        </h5>
                                                        <span className="text-xs font-mono text-emerald-400 bg-emerald-900/20 px-2 py-0.5 rounded border border-emerald-500/20">
                                                            £{(rec.suggested_spend ?? 0).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    
                                                    <p className="text-xs text-slate-300 mb-4 leading-relaxed h-10 overflow-hidden">
                                                        {rec.rationale}
                                                    </p>

                                                    <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Est. Impact</span>
                                                            <span className="text-sm font-bold text-white">+{rec.projected_votes} Votes</span>
                                                        </div>
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Efficiency</span>
                                                            <span className="text-sm font-mono text-slate-300">£{rec.projected_cost_per_vote?.toFixed(2)} CPV</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 2. STRATEGIC CANVASSING */}
{activeStrategicTab === 'canvassing' && hasCanvass && (
    <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* LEFT COLUMN: Rationale, Stats & Timing */}
            <div className="flex flex-col justify-between gap-4">
                
                {/* Operational Rationale */}
                <div>
                    <h6 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Operational Rationale</h6>
                    <p className="text-sm text-slate-300 leading-relaxed">
                        {stratCanvass.rationale}
                    </p>
                </div>
                
                {/* 3-Column Stats Grid */}
                <div className="grid grid-cols-3 gap-3">
                    {/* Col 1: Investment */}
                    <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 flex flex-col justify-center">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Target Invest.</div>
                        <div className="text-lg font-bold text-white font-mono leading-none">
                            {stratCanvass.resource_note ? (
                                stratCanvass.resource_note.match(/\d+(\.\d+)?/)?.[0] || '0'
                            ) : '0'} <span className="text-xs font-normal text-slate-400">Hrs</span>
                        </div>
                    </div>

                    {/* Col 2: Impact */}
                    <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 flex flex-col justify-center">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Est. Impact</div>
                        <div className="text-lg font-bold text-white font-mono leading-none">
                            +{stratCanvass.projected_votes} <span className="text-xs font-normal text-slate-400">Votes</span>
                        </div>
                    </div>

                    {/* Col 3: Recommended Time (Generic) */}
                    <div className="bg-blue-900/10 p-3 rounded border border-blue-500/20 flex flex-col justify-center relative overflow-hidden">
                        <div className="text-[10px] text-blue-300/70 uppercase tracking-wider mb-1">Standard Window</div>
                        <div className="text-xs font-bold text-blue-100 leading-snug">
                            {stratCanvass.recommended_time_window || 'Standard Hours'}
                        </div>
                    </div>
                </div>

                {/* Timing Rationale (Generic) */}
                {stratCanvass.timing_rationale && (
                    <div className="pt-2">
                        <h6 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                            <Timer size={12}/> Timing Logic
                        </h6>
                        <p className="text-xs text-slate-400 leading-relaxed border-l-2 border-slate-700 pl-3 italic">
                            {stratCanvass.timing_rationale}
                        </p>
                    </div>
                )}
            </div>

            {/* RIGHT COLUMN: Interactive Flighting Schedule Chart */}
            {stratCanvass.weekly_schedule && (
                <div className="bg-slate-800/40 p-4 rounded border border-slate-700/50 flex flex-col h-full relative">
                    <div className="flex justify-between items-start mb-3">
                        <h6 className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                            <Calendar size={14}/> Flighting Schedule
                        </h6>
                        <div className="text-[10px] text-slate-500 flex items-center gap-1">
                            <MousePointer size={10} /> Click bars for tactical detail
                        </div>
                    </div>
                    
                    {/* CHART AREA */}
                    <div className="flex-1 overflow-x-auto pb-2 custom-scrollbar flex items-end min-h-[160px]">
                        <div className="flex items-end gap-2 h-40 pt-4 px-2 min-w-max w-full">
                            {(() => {
                                const scheduleEntries = Object.entries(stratCanvass.weekly_schedule);
                                // Handle both object structure and legacy number structure for max calc
                                const maxHours = Math.max(...scheduleEntries.map(([, data]) => 
                                    typeof data === 'object' ? (data.hours || 0) : data
                                )) || 1;
                                
                                return scheduleEntries.map(([week, data]) => {
                                    // Normalize Data
                                    const hours = typeof data === 'object' ? (data.hours || 0) : data;
                                    const isComplex = typeof data === 'object';
                                    const isSelected = selectedCanvassWeek === week;
                                    
                                    return (
                                        <button 
                                            key={week} 
                                            onClick={() => setSelectedCanvassWeek(isSelected ? null : week)}
                                            className="flex flex-col items-center gap-2 group w-12 h-full justify-end focus:outline-none"
                                        >
                                            <div className="w-full flex-1 bg-slate-700/20 rounded-t-lg relative flex items-end overflow-hidden transition-all duration-300">
                                                {/* Bar */}
                                                <div 
                                                    className={`w-full rounded-t transition-all duration-300 relative
                                                        ${isSelected 
                                                            ? 'bg-gradient-to-t from-blue-500 to-cyan-200 opacity-100 shadow-[0_0_15px_rgba(34,211,238,0.4)]' 
                                                            : 'bg-gradient-to-t from-blue-600 to-cyan-400 opacity-80 group-hover:opacity-100'
                                                        }`} 
                                                    style={{ height: `${Math.max((hours / maxHours) * 100, 4)}%` }}
                                                >
                                                    {/* Selection Indicator dot */}
                                                    {isSelected && (
                                                        <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full"></div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* X-Axis Label */}
                                            <div className="flex flex-col items-center space-y-0.5">
                                                <span className={`text-[10px] font-medium uppercase tracking-tight transition-colors ${isSelected ? 'text-white font-bold' : 'text-slate-500'}`}>
                                                    {week.replace(/Week\s?|W/i, 'W')}
                                                </span>
                                                <span className={`text-xs font-mono transition-colors ${isSelected ? 'text-cyan-400 font-bold' : 'text-slate-200'}`}>
                                                    {hours.toFixed(0)}<span className="text-[9px] font-normal ml-0.5 opacity-50">h</span>
                                                </span>
                                            </div>
                                        </button>
                                    );
                                });
                            })()}
                        </div>
                    </div>

                    {/* DYNAMIC DETAIL PANEL (Appears when a week is selected) */}
                    {selectedCanvassWeek && stratCanvass.weekly_schedule[selectedCanvassWeek] && typeof stratCanvass.weekly_schedule[selectedCanvassWeek] === 'object' && (
                        <div className="mt-3 p-3 bg-slate-900/60 border-t border-blue-500/30 rounded-b-lg animate-in slide-in-from-top-2 fade-in duration-200">
                            {(() => {
                                const details = stratCanvass.weekly_schedule[selectedCanvassWeek];
                                return (
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-start">
                                            <span className="text-xs font-bold text-white uppercase tracking-wide">
                                                {selectedCanvassWeek} Specifics
                                            </span>
                                            {details.window && (
                                                <span className="text-[10px] font-mono text-cyan-300 bg-cyan-900/20 px-1.5 py-0.5 rounded border border-cyan-500/20 flex items-center gap-1">
                                                    <Clock size={10} /> {details.window}
                                                </span>
                                            )}
                                        </div>
                                        
                                        {details.rationale_short && (
                                            <p className="text-xs text-slate-300 leading-snug">
                                                "{details.rationale_short}"
                                            </p>
                                        )}

                                        <div className="flex items-center gap-4 pt-1">
                                            {details.volunteers_needed && (
                                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                    <Users size={12} className="text-blue-400"/>
                                                    <span className="text-white font-mono font-bold">{details.volunteers_needed}</span> 
                                                    <span>Volunteers Required</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })()}
                        </div>
                    )}
                </div>
            )}
        </div>
        
        {stratCanvass.resource_note && (
            <div className="text-xs text-blue-300/80 italic border-t border-blue-500/20 pt-3">
                <Info size={12} className="inline mr-1 mb-0.5"/>
                Note: {stratCanvass.resource_note}
            </div>
        )}
    </div>
)}

                                {/* 3. RESONANCE STRATEGIC OVERLAY */}
                                {activeStrategicTab === 'resonance' && hasResonance && (
                                    <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
                                        {/* 1. Psychographic Hero Card */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Driver */}
                                            <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 flex flex-col justify-center items-center text-center">
                                                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Dominant Driver</span>
                                                <div className="text-lg font-bold text-white mb-1">{currentWardResonance.psychographic_driver || 'N/A'}</div>
                                                <div className="text-xs text-slate-400">Primary decision-making heuristic</div>
                                            </div>

                                            {/* Cognitive Route */}
                                            <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 flex flex-col justify-center items-center text-center">
                                                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Cognitive Route</span>
                                                <div className="flex items-center gap-2 mb-1">
                                                    {currentWardResonance.cognitive_route === 'central_route' ? <BookOpen size={18} className="text-cyan-400"/> : <Eye size={18} className="text-fuchsia-400"/>}
                                                    <div className={`text-lg font-bold ${currentWardResonance.cognitive_route === 'central_route' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>
                                                        {currentWardResonance.cognitive_route === 'central_route' ? 'Central' : 'Peripheral'}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-slate-400">
                                                    {currentWardResonance.cognitive_route === 'central_route' ? 'Logic & Argument Driven' : 'Emotion & Cue Driven'}
                                                </div>
                                            </div>

                                            {/* Synergy Score */}
                                            <div className="bg-gradient-to-br from-indigo-900/40 to-slate-800/60 p-4 rounded-xl border border-indigo-500/30 flex flex-col justify-center items-center text-center relative overflow-hidden">
                                                <div className="absolute inset-0 bg-indigo-500/5 animate-pulse"></div>
                                                <span className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-2 relative z-10">Global Synergy</span>
                                                <div className="text-2xl font-black text-white mb-1 relative z-10">
                                                    {currentWardResonance.global_synergy_score?.toFixed(2)}x
                                                </div>
                                                <div className="text-xs text-indigo-200/70 relative z-10">Multi-channel multiplier</div>
                                            </div>
                                        </div>

                                        {/* 2. Deployment Directives */}
                                        <div>
                                            <h5 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
                                                <GitMerge size={16} className="text-slate-400"/> Deployment Directives
                                            </h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {(currentWardResonance.deployment_directives || []).map((dir, idx) => (
                                                    <div key={idx} className="bg-slate-800/40 border border-slate-700/50 p-3 rounded-lg hover:border-slate-600 transition-colors">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 border border-slate-600">
                                                                {dir.type}
                                                            </span>
                                                            <span className="text-[10px] text-slate-500">Conf: {(dir.confidence_score * 100).toFixed(0)}%</span>
                                                        </div>
                                                        <p className="text-sm font-medium text-white mb-1 leading-snug">"{dir.instruction}"</p>
                                                        <p className="text-xs text-slate-400 leading-relaxed mb-2">{dir.rationale}</p>
                                                        <div className="text-[10px] text-slate-600 italic border-t border-slate-700/50 pt-2 mt-auto">
                                                            Basis: {dir.scientific_basis}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* 3. Neuro-Risk Factors */}
                                            {currentWardResonance.neuro_risk_factors?.length > 0 && (
                                                <div className="bg-red-900/10 border border-red-500/20 rounded-lg p-4">
                                                    <h6 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                        <AlertOctagon size={14}/> Neuro-Risk Factors
                                                    </h6>
                                                    <ul className="space-y-2">
                                                        {currentWardResonance.neuro_risk_factors.map((risk, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-xs text-red-200/80">
                                                                <span className="mt-1 w-1 h-1 rounded-full bg-red-500 shrink-0"></span>
                                                                {risk}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* 4. Top Synergies */}
                                            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
                                                <h6 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <Layers size={14}/> Top Interactions
                                                </h6>
                                                <div className="space-y-2">
                                                    {(currentWardResonance.synergy_matrix || [])
                                                        .filter(s => Math.abs(s.final_synergy_score) > 0.1) // Only significant ones
                                                        .slice(0, 3)
                                                        .map((syn, i) => (
                                                            <div key={i} className="flex justify-between items-center text-xs">
                                                                <span className="text-slate-300">
                                                                    {formatChannelName(syn.pair[0])} + {formatChannelName(syn.pair[1])}
                                                                </span>
                                                                <span className={`font-mono font-bold ${syn.final_synergy_score > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                    {syn.final_synergy_score > 0 ? '+' : ''}{syn.final_synergy_score.toFixed(2)}
                                                                </span>
                                                            </div>
                                                    ))}
                                                    {(currentWardResonance.synergy_matrix || []).filter(s => Math.abs(s.final_synergy_score) > 0.1).length === 0 && (
                                                        <p className="text-xs text-slate-500 italic">No strong channel interactions detected.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Ward Deep Dive (Original Features Retained + Multi-Ward Switcher) */}
                    {wardAnalysis ? (
                        <div className="panel p-6 bg-slate-900/40 border border-slate-800 rounded-xl transition-all duration-300">
                            
                            {/* --- Ward Selector Header --- */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                                <div className="flex items-center space-x-3">
                                    <MapPin className="text-accent-400" size={24}/>
                                    <div>
                                        <h4 className="text-xl font-semibold text-white">Ward Deep Dive</h4>
                                        <div className="text-sm text-slate-400">{wardAnalysis?.ward_metadata?.ward_name ?? 'N/A'}</div>
                                    </div>
                                </div>
                                
                                {/* WARD SWITCHER UI */}
                                {wardKeys.length > 1 && (
                                    <div className="flex space-x-2 overflow-x-auto pb-1 custom-scrollbar">
                                        {wardKeys.map(key => {
                                            const wName = results?.ward_level_analysis?.[key]?.ward_metadata?.ward_name || key;
                                            const isActive = key === currentWardId;
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => setSelectedWardId(key)}
                                                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                                                        isActive 
                                                        ? 'bg-accent-500 text-white' 
                                                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                    }`}
                                                >
                                                    {wName}
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-400 border-b border-slate-700 pb-4 mb-4">
                                <span>Pop: <span className="text-white font-mono">{(wardAnalysis?.demographic_intelligence?.population ?? 0).toLocaleString()}</span></span>
                                <span>Avg Age: <span className="text-white font-mono">{(wardAnalysis?.demographic_intelligence?.average_age ?? 0).toFixed(1)}</span></span>
                                <span>Turnout: <span className="text-white font-mono">{((wardAnalysis?.demographic_intelligence?.expected_turnout ?? 0) * 100).toFixed(1)}%</span></span>
                                <span>Med. Income: <span className="text-white font-mono">£{((wardAnalysis?.demographic_intelligence?.median_income ?? 0) / 1000).toFixed(0)}k</span></span>
                                <span>Swing: <span className="text-white font-mono text-danger-300">{((wardAnalysis?.demographic_intelligence?.swing_required ?? 0) * 100).toFixed(1)}%</span></span>
                                <span>Volatility: <span className="text-white font-mono">{((wardAnalysis?.demographic_intelligence?.demographic_volatility ?? 0) * 100).toFixed(1)}%</span></span>
                                <span className="text-blue-300">Youth Index: <span className="text-white font-mono">{((wardAnalysis?.demographic_intelligence?.youth_index ?? 0) * 100).toFixed(1)}%</span></span>
                                <span className="text-orange-300">Senior Index: <span className="text-white font-mono">{((wardAnalysis?.demographic_intelligence?.senior_index ?? 0) * 100).toFixed(1)}%</span></span>
                            </div>

                            {/* === INSERTION START: DIAMOND DATA VISUALIZATION === */}
                                {wardAnalysis?.strategic_leverage && (
                                <div className="animate-in slide-in-from-bottom-2 duration-500 mb-6">
                                 <StrategicLeverageGauge leverageData={wardAnalysis.strategic_leverage} />
                             </div>
                            )}

                            {/* --- MOVED: STRATEGIC ALIGNMENT CHECK --- */}
                            <div className={`mb-6 rounded-lg p-4 border ${alignLevel === 'High' ? 'bg-green-500/10 border-green-500/30' : alignLevel === 'Medium' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                        <Link size={16} className={`${alignLevel === 'High' ? 'text-green-400' : alignLevel === 'Medium' ? 'text-yellow-400' : 'text-red-400'}`}/>
                                        <h5 className="font-semibold text-white">Strategic Alignment Check</h5>
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
                                        Strategy vs. Reality
                                    </div>
                                </div>
                                
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className={`text-xl font-bold mb-1 ${alignLevel === 'High' ? 'text-green-300' : alignLevel === 'Medium' ? 'text-yellow-300' : 'text-red-300'}`}>
                                            {alignLabel}
                                        </div>
                                        <p className="text-xs text-slate-300 leading-relaxed">{alignRationale}</p>
                                    </div>
                                    
                                    <div className="md:text-right md:border-l md:border-white/10 md:pl-4 min-w-[140px]">
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Focus Strategy</div>
                                        <div className="text-xs font-medium text-white mb-2">{results?.input_summary?.campaign_focus}</div>
                                        
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Reality Tier</div>
                                        <div className="text-xs font-medium text-white">{wardContext?.artemis_strategy?.tier ?? 'Unknown'}</div>
                                    </div>
                                </div>
                            </div>
                        
                            
                            <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 mb-6">
                                <h5 className="text-base font-semibold text-white mb-3">Ward Strategic Context</h5>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                    <WardStat icon={<Key size={20}/>} label="Artemis Tier" value={(wardContext?.artemis_strategy?.tier ?? 'N/A').replace(/_/g, ' ')} />
                                    <WardStat icon={<TrendingUp size={20}/>} label="Winability Score" value={`${((wardContext?.artemis_strategy?.winability_score ?? 0) * 100).toFixed(1)}%`} />
                                    <WardStat icon={<Target size={20}/>} label="Theia Archetype" value={wardContext?.theia_demographics?.primary_archetype_name ?? 'N/A'} />
                                </div>
                                <h6 className="text-sm font-semibold text-white mb-3">Target Groups</h6>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                    <WardStat icon={<UserPlus size={20}/>} label="Persuadable Voters" value={(wardContext?.artemis_strategy?.targetable_voters?.persuadable_voters ?? 0).toLocaleString()} />
                                    <WardStat icon={<Heart size={20}/>} label="Supporter Base" value={(wardContext?.artemis_strategy?.targetable_voters?.supporter_base ?? 0).toLocaleString()} />
                                    <WardStat icon={<ArrowUpCircle size={20}/>} label="Turnout Potential" value={(wardContext?.artemis_strategy?.targetable_voters?.turnout_potential ?? 0).toLocaleString()} />
                                </div>
                                
                                {/* --- Social & Tactical Atmosphere --- */}
                                <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 mb-6">
                                    <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                                        <Users size={18} className="text-purple-400"/>
                                        Social & Tactical Atmosphere
                                    </h5>
                                    
                                    {(() => {
                                        const topChannel = wardAnalysis?.ranked_channels?.[0]?.channel;
                                        const rawFactors = wardAnalysis?.channel_performance?.[topChannel]?.contributing_factors || {};
                                        const socialScore = rawFactors.social_dynamics_effect;
                                        const strategicScore = rawFactors.strategic_voting_boost;
                                        const artemisTier = wardContext?.artemis_strategy?.tier;
                                        const atmosphere = getSocialAtmosphere(socialScore, strategicScore, artemisTier);
                                        
                                        return (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Social Dynamic Card */}
                                                <div className="bg-slate-900/50 rounded p-3 border-l-2 border-l-purple-500">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className="text-xs text-slate-400 uppercase tracking-wider">Prevailing Mood</span>
                                                        <div className={atmosphere.color}>{atmosphere.icon}</div>
                                                    </div>
                                                    <div className={`text-sm font-bold ${atmosphere.color} mb-1`}>{atmosphere.label}</div>
                                                    <div className="text-xs text-slate-300 leading-relaxed">{atmosphere.desc}</div>
                                                    {socialScore !== undefined && (
                                                        <div className="mt-2 text-[10px] font-mono text-slate-500">
                                                            Net Impact: {socialScore > 0 ? '+' : ''}{(socialScore * 100).toFixed(2)}%
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Strategic/Tactical Card */}
                                                <div className="bg-slate-900/50 rounded p-3 border-l-2 border-l-cyan-500">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className="text-xs text-slate-400 uppercase tracking-wider">Tactical Landscape</span>
                                                        <Brain size={20} className="text-cyan-400"/>
                                                    </div>
                                                    <div className="text-sm font-bold text-white mb-1">
                                                        {strategicScore > 0.005 ? "Active Tactical Voting" : "Straight Choice"}
                                                    </div>
                                                    <div className="text-xs text-slate-300 leading-relaxed">
                                                        {strategicScore > 0.005 
                                                            ? "Education-weighted model predicts voters are voting strategically to block opponents." 
                                                            : "Voters are voting primarily on party preference rather than tactical blocking."}
                                                    </div>
                                                    {wardContext?.demographic_intelligence?.swing_required && (
                                                        <div className="mt-2 text-[10px] font-mono text-slate-500">
                                                            Swing Req: {(wardContext.demographic_intelligence.swing_required * 100).toFixed(1)}%
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                
                                {/* Strategic Synthesis (Horizontal KPI Scroll) */}
                                <h6 className="text-sm font-semibold text-white mb-2">Strategic Synthesis</h6>
                                <div className="flex space-x-4 overflow-x-auto pb-4 mb-2 custom-scrollbar">
                                    {tacticalRecs.length > 0 ? (
                                        tacticalRecs.map((rec, i) => (
                                            <div key={i} className="min-w-[220px] w-[220px] flex-shrink-0 bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <div className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center text-accent-400 text-xs font-bold">{i + 1}</div>
                                                    <h6 className="font-semibold text-xs text-white">Tactical Order</h6>
                                                </div>
                                                <p className="text-xs text-slate-300 leading-relaxed">{rec}</p>
                                            </div>
                                        ))
                                    ) : (<p className="text-xs text-slate-500">No specific strategic synthesis available.</p>)}
                                </div>

                                {/* Raw Engine Heuristics (Horizontal KPI Scroll) */}
                                <h6 className="text-sm font-semibold text-white mb-2">Raw Engine Heuristics</h6>
                                <div className="flex space-x-4 overflow-x-auto pb-4 custom-scrollbar">
                                    {(() => {
                                        const topChannelName = wardAnalysis?.ranked_channels?.[0]?.channel;
                                        const factors = wardAnalysis?.channel_performance?.[topChannelName]?.contributing_factors;
                                        if (!factors) return <p className="text-xs text-slate-500">No raw heuristics generated.</p>;
                                        
                                        return Object.entries(factors)
                                            .filter(([_, val]) => Math.abs(val) > 0.001)
                                            .sort(([_, a], [__, b]) => Math.abs(b) - Math.abs(a))
                                            .slice(0, 5)
                                            .map(([key, val]) => (
                                                <div key={key} className="min-w-[160px] flex-shrink-0 bg-slate-800/40 p-3 rounded-lg border border-slate-700/50 flex flex-col justify-center">
                                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 truncate" title={key.replace(/_/g, ' ')}>{key.replace(/_/g, ' ')}</span>
                                                    <span className={`text-lg font-bold font-mono ${val > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {val > 0 ? '+' : ''}{val.toFixed(2)}
                                                    </span>
                                                </div>
                                            ));
                                    })()}
                                </div>
                            </div>

                            <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 mb-6">
                                <h5 className="text-base font-semibold text-white mb-4">Ovid Messaging Guidance</h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <h6 className="flex items-center space-x-2 text-sm font-semibold text-slate-200 mb-2"><MapPin size={16} className="text-accent-400" /><span>Key Issues</span></h6>
                                        <ul className="space-y-1 text-xs text-slate-300 list-disc list-inside">
                                            {(wardContext?.ovid_messaging?.key_issues ?? []).length > 0 ? 
                                                (wardContext?.ovid_messaging?.key_issues ?? []).map((issue, i) => (
                                                    <li key={i}>{typeof issue === 'object' ? (issue.issue || issue.description) : issue}</li>
                                                ))
                                                : <li>No contextual issue data available.</li>
                                            }
                                        </ul>
                                    </div>
                                    <div>
                                        <h6 className="flex items-center space-x-2 text-sm font-semibold text-success-300 mb-2"><Check size={16} /><span>Resonates With</span></h6>
                                        <ul className="space-y-1 text-xs text-slate-300 list-disc list-inside">
                                            {(wardContext?.ovid_messaging?.resonates_with ?? []).map((point, i) => (
                                                <li key={i}>{typeof point === 'object' ? (point.text || point.description || JSON.stringify(point)) : point}</li>
                                            ))}
                                            {(wardContext?.ovid_messaging?.resonates_with ?? []).length === 0 && <li>No specific resonance data.</li>}
                                        </ul>
                                    </div>
                                    <div>
                                        <h6 className="flex items-center space-x-2 text-sm font-semibold text-danger-300 mb-2"><XCircle size={16} /><span>Avoid</span></h6>
                                        <ul className="space-y-1 text-xs text-slate-300 list-disc list-inside">
                                            {(wardContext?.ovid_messaging?.avoid ?? []).map((point, i) => (
                                                <li key={i}>{typeof point === 'object' ? (point.text || point.description || JSON.stringify(point)) : point}</li>
                                            ))}
                                            {(wardContext?.ovid_messaging?.avoid ?? []).length === 0 && <li>No specific avoidance data.</li>}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <h5 className="text-base font-semibold text-white mb-3">Channel Analysis</h5>
                            <div className="space-y-3">
                                {sortedChannels.map(channel => {
                                    const fullChannelData = wardAnalysis?.channel_performance?.[channel.channel] ?? {};
                                    
                                    // --- Find Budget Allocation for this specific channel ---
                                    const allocationData = results?.campaign_optimization?.optimal_budget_allocation?.allocations?.find(a => a.channel === channel.channel);
                                    const hasAllocation = allocationData && (allocationData.allocated_budget > 0 || allocationData.allocated_hours > 0);
                                    const mode = allocationData?.resource_mode ?? 'financial_only';

                                    const currentView = channelViewMode[channel.channel] || 'age';
                                    const setView = (mode) => setChannelViewMode(prev => ({ ...prev, [channel.channel]: mode }));
                                    const archetypeFitData = (currentView === 'archetype') ? calculateArchetypeFit(wardContext?.theia_demographics?.archetype_mix, fullChannelData?.demographic_analysis) : [];
                                    const ci = fullChannelData.conversion_ci;

                                    // Generate Frontend Insights
                                    const { target: optimalTarget, rationale: demoRationale } = generateDemographicInsights(fullChannelData?.demographic_analysis);

                                    // Fix: Correctly grab Cost Per Vote from full ROI analysis, not lightweight summary
                                    const realCostPerVote = fullChannelData?.temp_roi_analysis?.cost_per_vote ?? 0;
                                    const realVotesPerHour = fullChannelData?.temp_roi_analysis?.votes_per_hour ?? 0;

                                    // Determine Gradient for Mini Bar
                                    let barGradient = "bg-gradient-to-r from-indigo-500 to-purple-500";
                                    if (hasAllocation) {
                                        if (mode === 'time_only') barGradient = "bg-gradient-to-r from-blue-400 to-cyan-500";
                                        else if (mode === 'hybrid') barGradient = "bg-gradient-to-r from-purple-500 to-fuchsia-500";
                                        else barGradient = "bg-gradient-to-r from-emerald-400 to-teal-500";
                                    }

                                    return (
                                    <div key={channel.channel} className={`rounded-lg border transition-all duration-200 ${hasAllocation ? 'bg-slate-800 border-slate-700' : 'bg-slate-800/50 border-slate-700/50 opacity-80'}`}>
                                        
                                        {/* --- UPDATED: Cleaner, Grid-Based Collapsed View --- */}
                                        <button onClick={() => setOpenChannel(openChannel === channel.channel ? null : channel.channel)} className="w-full p-4 block text-left group">
                                            <div className="flex items-center justify-between">
                                                {/* Left: Identity */}
                                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                                    <div className={`${hasAllocation ? 'text-accent-400' : 'text-slate-600'} transition-colors`}>
                                                        {getChannelIcon(channel.channel)}
                                                    </div>
                                                    <div className="truncate pr-4">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-bold text-white truncate">{formatChannelName(channel.channel)}</span>
                                                            {hasAllocation && (
                                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono border flex items-center gap-1 ${mode === 'time_only' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : mode === 'hybrid' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
                                                                    {mode === 'time_only' ? <Clock size={10}/> : <DollarSign size={10}/>}
                                                                    {/* Active Badge REMOVED per request */}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-500 truncate hidden sm:block">
                                                            {hasAllocation 
                                                                ? `Utilizes ${allocationData.allocation_percentage?.toFixed(1)}% of total campaign capacity` 
                                                                : 'No resources allocated'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Metrics Grid (Clean Alignment) */}
                                                <div className="flex items-center gap-6 lg:gap-8 mr-4">
                                                    {/* Metric 1: Fit (With Bar) */}
                                                    <div className="hidden sm:flex flex-col items-end w-24">
                                                        <div className="flex justify-between w-full mb-1">
                                                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fit</span>
                                                            <span className="font-mono font-bold text-white text-xs">{((channel.demographic_fit ?? 0) * 100).toFixed(0)}%</span>
                                                        </div>
                                                        <ModernMiniGauge score={(channel.demographic_fit ?? 0) * 100} gradientClass={barGradient} />
                                                    </div>

                                                    {/* Metric 2: Allocation (New Position) */}
                                                    <div className="hidden sm:flex flex-col items-end w-24">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Alloc.</span>
                                                        <span className="font-mono font-bold text-emerald-400 text-xs">
                                                            {hasAllocation ? formatResourceValue(allocationData.allocated_budget, allocationData.allocated_hours, mode) : '-'}
                                                        </span>
                                                    </div>

                                                    {/* Metric 3: Efficiency (CPV or VPH) */}
                                                    <div className="hidden sm:flex flex-col items-end w-20">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Eff.</span>
                                                        {realVotesPerHour > 0 ? (
                                                            <span className="font-mono font-bold text-blue-300">{realVotesPerHour.toFixed(1)} VPH</span>
                                                        ) : (
                                                            <span className="font-mono font-bold text-slate-300">{realCostPerVote > 0 ? `£${realCostPerVote.toFixed(2)}` : '-'}</span>
                                                        )}
                                                    </div>

                                                    {/* Metric 4: Confidence */}
                                                    <div className="hidden md:flex flex-col items-end w-12">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Conf</span>
                                                        <span className={`font-mono font-bold ${fullChannelData.confidence_score > 0.8 ? 'text-emerald-400' : fullChannelData.confidence_score < 0.5 ? 'text-yellow-400' : 'text-slate-300'}`}>
                                                            {((fullChannelData.confidence_score ?? 0) * 100).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Chevron */}
                                                <div className={`transition-transform duration-300 text-slate-500 group-hover:text-white ${openChannel === channel.channel ? 'rotate-180' : ''}`}>
                                                    <ChevronDown size={20} />
                                                </div>
                                            </div>
                                        </button>

                                        {openChannel === channel.channel && (
                                            <div className="p-4 border-t border-slate-700 bg-slate-900/50">
                                                
                                                {/* --- UPDATED: Dynamic Resource Application Panel --- */}
                                                {hasAllocation ? (
                                                    <div className={`mb-6 rounded-lg p-3 flex justify-between items-center border ${mode === 'time_only' ? 'bg-blue-900/10 border-blue-500/20' : mode === 'hybrid' ? 'bg-purple-900/10 border-purple-500/20' : 'bg-emerald-900/10 border-emerald-500/20'}`}>
                                                        <div>
                                                            <div className={`text-xs uppercase tracking-wider font-semibold mb-1 ${mode === 'time_only' ? 'text-blue-400' : mode === 'hybrid' ? 'text-purple-400' : 'text-emerald-400'}`}>
                                                                {mode === 'time_only' ? 'Volunteer Investment' : mode === 'hybrid' ? 'Hybrid Investment' : 'Financial Application'}
                                                            </div>
                                                            <div className="text-2xl font-bold text-white font-mono">
                                                                {formatResourceValue(allocationData.allocated_budget, allocationData.allocated_hours, mode)}
                                                            </div>
                                                            <div className="text-[10px] text-slate-400"> 
                                                                Represents <span className="text-white font-bold">{allocationData.allocation_percentage.toFixed(1)}%</span> of total capacity
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                             <div className="text-xs text-slate-400 mb-1">Est. Impact</div>
                                                             <div className="text-lg font-bold text-white">~{allocationData.expected_votes.toFixed(0)} Votes</div>
                                                             {/* Show VPH in box if relevant */}
                                                             {realVotesPerHour > 0 && (
                                                                <div className="text-[10px] text-blue-300 font-mono">
                                                                    {realVotesPerHour.toFixed(1)} Votes / Hour
                                                                </div>
                                                             )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                     <div className="mb-6 bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-center gap-3">
                                                        <div className="bg-slate-700 p-2 rounded-full text-slate-400"><DollarSign size={16}/></div>
                                                        <div>
                                                            <div className="text-sm font-semibold text-slate-300">No Resources Allocated</div>
                                                            <div className="text-xs text-slate-500">Model optimization prioritized other channels for higher ROI.</div>
                                                        </div>
                                                     </div>
                                                )}

                                                {ci && (
                                                    <div className="mb-4 p-2 bg-slate-800/50 rounded border border-slate-700 flex justify-between items-center text-xs">
                                                        <span className="text-slate-400">Statistical Confidence (95% CI):</span>
                                                        <span className="font-mono text-accent-300">{(ci.lower_95 * 100).toFixed(2)}% - {(ci.upper_95 * 100).toFixed(2)}% conversion rate</span>
                                                    </div>
                                                )}

                                                {/* --- Psychometric Depth & Momentum --- */}
                                                {fullChannelData.sophisticated_research_metrics && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                        {(() => {
                                                            const metrics = fullChannelData.sophisticated_research_metrics;
                                                            const psych = getInteractionPsychometrics(metrics);
                                                            
                                                            return (
                                                                <>
                                                                    {/* IRT / Resistance Analysis */}
                                                                    <div className="p-3 bg-slate-800/80 rounded border border-slate-700">
                                                                        <div className="flex items-center space-x-2 mb-2">
                                                                            <Gauge size={16} className="text-accent-400"/>
                                                                            <span className="text-xs font-semibold text-white">Interaction Friction (IRT)</span>
                                                                        </div>
                                                                        <div className="flex justify-between items-end mb-1">
                                                                            <span className={`text-sm font-bold ${psych.score < 30 ? 'text-red-400' : psych.score > 70 ? 'text-green-400' : 'text-yellow-400'}`}>
                                                                                {psych.level}
                                                                            </span>
                                                                            <span className="text-xs font-mono text-slate-500">{psych.score.toFixed(0)}/100 Propensity</span>
                                                                        </div>
                                                                        <div className="w-full bg-slate-700 h-1.5 rounded-full mb-2">
                                                                            <div className={`h-1.5 rounded-full ${psych.score < 30 ? 'bg-red-500' : psych.score > 70 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{width: `${psych.score}%`}}></div>
                                                                        </div>
                                                                        <p className="text-[11px] text-slate-300 leading-tight">{psych.narrative}</p>
                                                                    </div>

                                                                    {/* Momentum Efficiency */}
                                                                    <div className="p-3 bg-slate-800/80 rounded border border-slate-700">
                                                                        <div className="flex items-center space-x-2 mb-2">
                                                                            <Zap size={16} className="text-yellow-400"/>
                                                                            <span className="text-xs font-semibold text-white">Momentum Conversion</span>
                                                                        </div>
                                                                        <div className="flex justify-between items-end mb-1">
                                                                            <span className="text-sm font-bold text-white">
                                                                                {((metrics.vote_conversion_efficiency ?? 0.5) * 100).toFixed(0)}% Efficiency
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-[11px] text-slate-300 leading-tight mb-2">
                                                                            For every unit of momentum generated in this channel, {(metrics.vote_conversion_efficiency ?? 0).toFixed(2)} converts to a hard vote.
                                                                        </p>
                                                                        <div className="flex items-center space-x-2 text-[10px] text-slate-400 bg-slate-900/50 p-1.5 rounded">
                                                                            <BarChart2 size={12}/>
                                                                            <span>Temporal Phase: <span className="text-white">{formatChannelName(metrics.phase_focus)}</span></span>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                <div className="flex space-x-1 mb-4 border-b border-slate-700">
                                                    <button onClick={() => setView('age')} className={`py-2 px-4 text-sm font-medium ${currentView === 'age' ? 'border-b-2 border-accent-400 text-white' : 'text-slate-400'}`}>By Age Group</button>
                                                    <button onClick={() => setView('archetype')} className={`py-2 px-4 text-sm font-medium ${currentView === 'archetype' ? 'border-b-2 border-accent-400 text-white' : 'text-slate-400'}`}>By Archetype</button>
                                                </div>
                                                {currentView === 'age' && (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        
        {/* HEADER: Title + Gender Toggle Switch */}
        <div className="flex justify-between items-center mb-4 border-b border-slate-700/50 pb-2">
            <div>
                <h6 className="text-sm font-semibold text-white">Effectiveness by Age Cohort</h6>
                <p className="text-xs text-slate-400">Demographic contribution & profile.</p>
            </div>
            <div className="flex bg-slate-900/50 rounded-lg p-1 border border-slate-700">
                <button
                    onClick={() => setAgeGenderToggle('female')}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-all flex items-center gap-1.5 ${ageGenderToggle === 'female' ? 'bg-fuchsia-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}`}
                >
                    <Users size={12} /> Women
                </button>
                <button
                    onClick={() => setAgeGenderToggle('male')}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-all flex items-center gap-1.5 ${ageGenderToggle === 'male' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'}`}
                >
                    <Users size={12} /> Men
                </button>
            </div>
        </div>

        <div className="space-y-2 text-xs">
            {fullChannelData?.demographic_analysis ? Object.entries(fullChannelData.demographic_analysis.age_group_contributions ?? {}).map(([age, data]) => {
                const isExpanded = activeAgeGroup === age;
                const impactPct = data?.contribution_percentage ?? 0;
                
                // --- INTELLIGENCE LOOKUP ---
                const ageMap = {
                    '18-24': '18-24',
                    '25-39': '25-34', 
                    '40-59': '50-64', 
                    '60+':   '65+'
                };
                
                const jsonKey = ageMap[age] || '25-34';
                const cohortData = (typeof AGE_DATA !== 'undefined' ? AGE_DATA.find(d => d.age_range === jsonKey) : null) || {};
                const profile = cohortData?.profiles?.[ageGenderToggle] || {};

                return (
                    <div key={age} className="group">
                        
                        {/* 1. SIMPLE BAR CHART */}
                        <button 
                            onClick={() => setActiveAgeGroup(isExpanded ? null : age)}
                            className="flex items-center w-full text-left focus:outline-none py-1.5"
                        >
                            <span className="w-16 shrink-0 text-slate-400 font-mono text-xs font-bold mr-3" title={age}>
                                {age}
                            </span>
                            
                            <div className="flex-1 bg-slate-700/50 rounded-full h-4 mr-3 relative overflow-hidden">
                                <div 
                                    className={`h-4 rounded-full shadow-[0_0_10px_rgba(0,0,0,0.2)] transition-all duration-500 ease-out ${
                                        isExpanded 
                                            ? (ageGenderToggle === 'female' ? 'bg-fuchsia-500' : 'bg-blue-500') 
                                            : 'bg-gradient-to-r from-violet-500 to-purple-500 opacity-90 group-hover:opacity-100 group-hover:brightness-110'
                                    }`} 
                                    style={{ width: `${impactPct}%` }}
                                ></div>
                            </div>
                            
                            <span className={`w-12 text-right font-mono text-[11px] transition-colors ${isExpanded ? 'text-white font-bold' : 'text-slate-400'}`}>
                                {impactPct.toFixed(1)}%
                            </span>
                        </button>

                        {/* 2. ADVANCED INTELLIGENCE DOSSIER (Dropdown) */}
                        {isExpanded && (
                            <div className="mt-3 mb-5 ml-2 pl-4 border-l-2 border-slate-700 bg-slate-900/40 py-5 pr-5 rounded-r-lg animate-in slide-in-from-top-1 fade-in duration-200">
                                
                                {/* Header: Profile Name & Leaning */}
                                <div className="flex justify-between items-start mb-5 border-b border-slate-700/50 pb-4">
                                    <div>
                                        <div className={`text-[10px] uppercase font-bold tracking-wider mb-1.5 ${ageGenderToggle === 'female' ? 'text-fuchsia-400' : 'text-blue-400'}`}>
                                            {cohortData.cultural_designator || "Cohort Profile"}
                                        </div>
                                        <div className="text-base font-bold text-white leading-tight">
                                            {profile.display_name || "Standard Voter Profile"}
                                        </div>
                                    </div>
                                    <div className="text-right pl-6 max-w-[200px]">
                                        <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1.5">Political Leaning</div>
                                        <div className="text-xs font-semibold text-slate-300 leading-snug">
                                            {profile.political_leaning || "Moderate / Unknown"}
                                        </div>
                                    </div>
                                </div>

                                {/* Strategic Analysis (High Level) */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Activity size={14} className="text-slate-400"/>
                                        <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Strategic Analysis</span>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed border-l-2 border-slate-700 pl-3">
                                        {profile.strategic_analysis || "No strategic analysis available."}
                                    </p>
                                </div>

                                {/* Key Concerns & Mobilizers Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                                    <div className="bg-slate-800/40 p-4 rounded border border-slate-700/50">
                                        <h6 className="text-[10px] text-orange-400 uppercase font-bold tracking-wider mb-3 flex items-center gap-2">
                                            <AlertTriangle size={12}/> Top Anxieties
                                        </h6>
                                        <ul className="space-y-2">
                                            {(profile.key_concerns || []).slice(0, 3).map((item, i) => (
                                                <li key={i} className="text-xs text-slate-300 flex items-start gap-2 leading-relaxed">
                                                    <span className="text-orange-500 mt-1">•</span> {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="bg-slate-800/40 p-4 rounded border border-slate-700/50">
                                        <h6 className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider mb-3 flex items-center gap-2">
                                            <Zap size={12}/> Primary Mobilizers
                                        </h6>
                                        <ul className="space-y-2">
                                            {(profile.primary_mobilisers || []).slice(0, 3).map((item, i) => (
                                                <li key={i} className="text-xs text-slate-300 flex items-start gap-2 leading-relaxed">
                                                    <span className="text-emerald-500 mt-1">•</span> {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                {/* Intelligence Summary (Deep Dive - Moved Bottom) */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Brain size={14} className="text-slate-400"/>
                                        <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Intelligence Summary</span>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed border-l-2 border-slate-700 pl-3">
                                        {profile.intelligence_summary || "No intelligence data available for this segment."}
                                    </p>
                                </div>

                                {/* Volatility Footer */}
                                <div className="pt-4 border-t border-slate-700/50">
                                    <div className="flex flex-col gap-2">
                                        <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">
                                            Volatility Assessment
                                        </div>
                                        <div className={`text-xs leading-relaxed font-medium ${
                                            (profile.volatility_assessment || '').includes('HIGH') || (profile.volatility_assessment || '').includes('EXTREME') 
                                            ? 'text-red-400' 
                                            : 'text-yellow-400'
                                        }`}>
                                            {profile.volatility_assessment || "Standard stability metrics apply."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }) : (
                <p className="text-xs text-slate-500">No demographic breakdown available.</p>
            )}
        </div>
    </div>
)}
                                                {currentView === 'archetype' && (
                                                    <div>
                                                        <h6 className="text-sm font-semibold text-white mb-2">Effectiveness by Archetype</h6>
                                                        <div className="space-y-2 text-xs">
                                                            {archetypeFitData.length > 0 ? archetypeFitData.map((arch) => {
                                                                const isExpanded = activeArchetype === arch.name;
                                                                const popPct = arch.percentage ?? 0;
                                                                const fitScore = arch.fitScore ?? 0;

                                                                // --- DATA LOOKUP ---
                                                                const lookupKey = arch.name.toUpperCase().replace(/\s+/g, '_');
                                                                const profile = (wardContext?.theia_demographics?.archetype_definitions?.[lookupKey]) || 
                                                                                (typeof ARCHETYPE_DATA !== 'undefined' ? ARCHETYPE_DATA[lookupKey] : null) || {};
                                                                const bio = profile.political_biography || {};

                                                                // --- DYNAMIC VISUALS ---
                                                                // Color Logic: Map Fit Score to Gradient
                                                                let barGradient = "bg-slate-600";
                                                                let mandateColor = "text-slate-400";
                                                                let mandateLabel = "Neutral";

                                                                if (fitScore > 75) {
                                                                    barGradient = "bg-gradient-to-r from-emerald-500 to-teal-400"; // High Fit
                                                                    mandateColor = "text-emerald-400";
                                                                    mandateLabel = "Primary Acquisition Channel";
                                                                } else if (fitScore > 50) {
                                                                    barGradient = "bg-gradient-to-r from-blue-500 to-indigo-400"; // Moderate Fit
                                                                    mandateColor = "text-blue-400";
                                                                    mandateLabel = "Secondary Support";
                                                                } else {
                                                                    barGradient = "bg-gradient-to-r from-orange-500 to-red-400"; // Low Fit
                                                                    mandateColor = "text-orange-400";
                                                                    mandateLabel = "High Friction / Avoid";
                                                                }

                                                                return (
                                                                    <div key={arch.name} className="group">
                                                                        <button 
                                                                            onClick={() => setActiveArchetype(isExpanded ? null : arch.name)}
                                                                            className="flex items-center w-full text-left focus:outline-none py-1.5"
                                                                        >
                                                                            <span className="w-36 shrink-0 text-slate-400 font-mono text-[10px] leading-tight mr-3 block" title={arch.name}>
                                                                                {arch.name.replace(/_/g, ' ')}
                                                                            </span>
                                                                            
                                                                            {/* Bar Container */}
                                                                            <div className="flex-1 bg-slate-700/50 rounded-full h-4 mr-3 relative overflow-hidden">
                                                                                <div 
                                                                                    className={`h-4 rounded-full shadow-[0_0_10px_rgba(0,0,0,0.2)] transition-all duration-700 ease-out ${barGradient} ${isExpanded ? 'brightness-110' : 'opacity-80 group-hover:opacity-100'}`} 
                                                                                    style={{ width: `${popPct}%` }}
                                                                                ></div>
                                                                            </div>
                                                                            
                                                                            <span className={`w-10 text-right font-mono text-[11px] transition-colors ${isExpanded ? 'text-white font-bold' : 'text-slate-400'}`}>
                                                                                {popPct.toFixed(1)}%
                                                                            </span>
                                                                        </button>

                                                                        {isExpanded && (
                                                                            <div className="mt-1 mb-3 ml-36 pl-4 border-l-2 border-slate-700 bg-slate-900/40 py-3 pr-3 rounded-r animate-in slide-in-from-top-1 fade-in duration-200">
                                                                                <div className="flex justify-between items-start mb-3 border-b border-slate-700/50 pb-2">
                                                                                    <div>
                                                                                        <div className="text-[9px] text-slate-500 uppercase font-bold tracking-wider mb-0.5">Political Disposition</div>
                                                                                        <div className="text-sm font-bold text-white leading-tight">
                                                                                            {bio.political_leaning || "Unknown Disposition"}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-right pl-4">
                                                                                        <div className="text-[9px] text-slate-500 uppercase font-bold tracking-wider mb-0.5">Tactical Susceptibility</div>
                                                                                        <div className={`text-xs font-mono font-bold ${
                                                                                            (bio.tactical_voting_susceptibility || '').includes('High') ? 'text-emerald-400' : 
                                                                                            (bio.tactical_voting_susceptibility || '').includes('Low') ? 'text-red-400' : 'text-yellow-400'
                                                                                        }`}>
                                                                                            {bio.tactical_voting_susceptibility || "N/A"}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="mb-3">
                                                                                    <div className="text-[9px] text-slate-500 uppercase font-bold tracking-wider mb-1">Psychographic Profile</div>
                                                                                    <p className="text-xs text-slate-300 italic leading-relaxed">
                                                                                        "{bio.disposition || "No specific psychographic profile data available."}"
                                                                                    </p>
                                                                                </div>

                                                                                {bio.primary_mobilisers && (
                                                                                    <div className="mb-3 bg-slate-800/50 p-2.5 rounded border border-slate-700/50">
                                                                                        <div className="text-[9px] text-blue-400 uppercase font-bold tracking-wider mb-1.5 flex items-center gap-1.5">
                                                                                            <Zap size={10} /> Key Mobilizers
                                                                                        </div>
                                                                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                                                                            {bio.primary_mobilisers.map((mob, i) => (
                                                                                                <li key={i} className="text-[10px] text-slate-300 flex items-start leading-snug">
                                                                                                    <span className="mr-1.5 text-blue-500 mt-0.5">•</span> {mob}
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    </div>
                                                                                )}

                                                                                <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <Activity size={12} className={mandateColor}/>
                                                                                        <span className={`text-[10px] font-bold uppercase ${mandateColor}`}>
                                                                                            {mandateLabel}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="text-[10px] text-slate-500 font-mono">
                                                                                        Channel Affinity: {fitScore.toFixed(0)}/100
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }) : (
                                                                <div className="text-center py-4 border border-dashed border-slate-700 rounded">
                                                                    <p className="text-xs text-slate-500">Archetype data unavailable.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )})}
                            </div>
                        </div>
                    ) : (
                        <div className="panel p-6 text-center text-slate-400 italic">
                            Select a ward to view detailed analysis.
                        </div>
                    )}
                </div> 

                {/* --- START: Right Sidebar (WITH ALL SYNTHESIS PANELS) --- */}
                <div className="space-y-6">
                
                    {/* Ward Action Box */}
                    {wardAnalysis && (
                        <div className="panel p-4 bg-slate-800/50 border border-accent-500/30 transition-all duration-300">
                            <h5 className="font-semibold text-white mb-3 truncate">Top 3 Actions for {wardAnalysis.ward_metadata.ward_name}</h5>
                            <ul className="space-y-3">
                                <li className="flex items-start space-x-3"><div className="text-accent-400 pt-1"><CheckSquare size={16} /></div><div><span className="text-xs text-slate-400">Lead With</span><p className="text-sm font-semibold text-white">{formatChannelName(wardAnalysis.ranked_channels[0]?.channel)}</p></div></li>
                                <li className="flex items-start space-x-3">
                                    <div className="text-accent-400 pt-1"><Megaphone size={16} /></div>
                                    <div>
                                        <span className="text-xs text-slate-400">Lead On </span>
                                        <p className="text-sm font-semibold text-white">
                                            {(() => {
                                                const topIssue = wardContext?.ovid_messaging?.key_issues?.[0];
                                                if (!topIssue) return 'N/A';
                                                return typeof topIssue === 'object' ? (topIssue.issue || topIssue.description) : topIssue;
                                            })()}
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start space-x-3"><div className="text-accent-400 pt-1"><Key size={16} /></div><div><span className="text-xs text-slate-400">Intensity</span><p className="text-sm font-semibold text-white">{(wardContext?.artemis_strategy?.tier ?? 'N/A').replace(/_/g, ' ')}</p></div></li>
                            </ul>
                        </div>
                    )}

                    {/* --- UPDATED: Recommended Resource Architecture (Hybrid) --- */}
                    <div className="panel p-4 bg-slate-800/80 border border-slate-700">
                        <div className="flex items-center space-x-2 mb-3">
                            <PieChart size={16} className="text-emerald-400"/>
                            <h5 className="font-semibold text-white">Resource Architecture</h5>
                        </div>
                        <div className="space-y-3">
                            {(() => {
                                const allocations = results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? [];
                                const sortedAllocations = [...allocations].sort((a, b) => b.allocation_percentage - a.allocation_percentage);
                                
                                if (sortedAllocations.length === 0) return <p className="text-xs text-slate-500">No allocation data generated.</p>;

                                return sortedAllocations.map(alloc => {
                                    const isTime = alloc.resource_mode === 'time_only';
                                    const isHybrid = alloc.resource_mode === 'hybrid';
                                    const barColor = isTime ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-emerald-500';
                                    
                                    return (
                                        <div key={alloc.channel} className="group">
                                            <div className="flex justify-between items-center text-xs mb-1">
                                                <span className="font-medium text-slate-300 group-hover:text-white transition-colors truncate w-1/2" title={alloc.channel}>
                                                    {formatChannelName(alloc.channel)}
                                                </span>
                                                <div className="text-right">
                                                    {alloc.allocated_budget > 0 && <span className="block font-bold text-white">£{(alloc.allocated_budget).toLocaleString()}</span>}
                                                    {alloc.allocated_hours > 0 && <span className="block font-bold text-blue-300">{alloc.allocated_hours.toFixed(0)} Hrs</span>}
                                                </div>
                                            </div>
                                            <div className="w-full bg-slate-700/50 rounded-full h-1.5 flex items-center gap-2">
                                                <div className={`${barColor} h-1.5 rounded-full`} style={{ width: `${alloc.allocation_percentage ?? 0}%` }}></div>
                                            </div>
                                            <div className="text-[10px] text-right text-slate-500 mt-0.5">{(alloc.allocation_percentage ?? 0).toFixed(1)}% Share</div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="mt-4 pt-3 border-t border-slate-700/50 flex flex-col gap-1">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-400">Total Capacity</span>
                                <span className="font-mono font-bold text-slate-300">£{(results?.input_summary?.budget ?? 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-emerald-400 font-medium">Net Campaign Spend</span>
                                <span className="font-mono font-bold text-emerald-300">£{(results?.campaign_optimization?.optimal_budget_allocation?.total_budget_allocated ?? 0).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* --- NEW: TOUCHPOINT ANALYSIS KPI BOX (PORTRAIT AGGREGATE) --- */}
{(wardAnalysis?.touchpoint_analysis || Object.keys(results?.ward_level_analysis || {}).some(k => results.ward_level_analysis[k].touchpoint_analysis)) && (
    <div className="panel p-5 bg-slate-800/80 border border-slate-700 relative overflow-hidden group flex flex-col items-center text-center">
        
        {/* Header with Switcher */}
        <div className="w-full flex items-center justify-between mb-4 border-b border-slate-700/50 pb-3">
            <div className="flex items-center space-x-2">
                <Fingerprint size={16} className="text-orange-400"/>
                <h5 className="font-semibold text-white text-sm">Touchpoint Saturation</h5>
            </div>
            <div className="flex bg-slate-900 rounded p-0.5">
                <button 
                    onClick={() => setTouchpointView('ward')} 
                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all duration-200 ${touchpointView === 'ward' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    Ward
                </button>
                <button 
                    onClick={() => setTouchpointView('campaign')} 
                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all duration-200 ${touchpointView === 'campaign' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    Global
                </button>
            </div>
        </div>
        
        {(() => {
            // --- LOGIC: AGGREGATE CALCULATION ---
            let totalTouchpoints = 0;
            let physicalScore = 0;
            let digitalScore = 0;
            let channelCount = 0;

            if (touchpointView === 'ward') {
                // Single Ward Logic
                const data = wardAnalysis?.touchpoint_analysis || {};
                Object.values(data).forEach(stats => {
                    const val = stats.avg_touchpoints || 0;
                    totalTouchpoints += val;
                    if (stats.is_physical) physicalScore += val;
                    if (stats.is_digital) digitalScore += val;
                });
            } else {
                // Campaign Average Logic
                const wardIds = Object.keys(results?.ward_level_analysis || {});
                const totalWards = wardIds.length || 1;
                
                // 1. Sum up everything across all wards
                let campaignTotalSum = 0;
                let campaignPhysicalSum = 0;
                let campaignDigitalSum = 0;

                wardIds.forEach(wid => {
                    const wData = results.ward_level_analysis[wid].touchpoint_analysis || {};
                    Object.values(wData).forEach(stats => {
                        const val = stats.avg_touchpoints || 0;
                        campaignTotalSum += val;
                        if (stats.is_physical) campaignPhysicalSum += val;
                        if (stats.is_digital) campaignDigitalSum += val;
                    });
                });

                // 2. Divide by ward count to get the "Average Ward Experience"
                totalTouchpoints = campaignTotalSum / totalWards;
                physicalScore = campaignPhysicalSum / totalWards;
                digitalScore = campaignDigitalSum / totalWards;
            }

            // --- COLOR LOGIC ---
            // < 5: Low Saturation (Yellow), 5-12: Optimal (Green), > 12: High/Fatigue (Orange)
            let heroColor = "text-emerald-400";
            let heroLabel = "Optimal Saturation";
            let heroSub = "Voter engagement is balanced.";
            
            if (totalTouchpoints < 5.0) { 
                heroColor = "text-yellow-400"; 
                heroLabel = "Low Saturation";
                heroSub = "Increase frequency to break through.";
            } else if (totalTouchpoints > 12.0) { 
                heroColor = "text-orange-400"; 
                heroLabel = "High Saturation";
                heroSub = "Risk of voter fatigue detected.";
            }

            return (
                <div className="w-full space-y-5">
                    {/* HERO METRIC: TOTAL AVERAGE TOUCHPOINTS */}
                    <div>
                        <div className={`text-4xl font-black ${heroColor} font-mono tracking-tighter mb-1`}>
                            {totalTouchpoints.toFixed(1)}
                        </div>
                        <div className={`text-xs font-bold uppercase tracking-wider ${heroColor} mb-1`}>
                            Avg. Touchpoints / Voter
                        </div>
                        <p className="text-[10px] text-slate-400 max-w-[200px] mx-auto leading-tight">
                            {heroSub}
                        </p>
                    </div>

                    {/* BREAKDOWN BARS */}
                    <div className="grid grid-cols-2 gap-3 w-full pt-4 border-t border-slate-700/50">
                        {/* Physical */}
                        <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50 flex flex-col items-center">
                            <span className="text-[10px] uppercase text-slate-500 font-bold mb-1">Physical</span>
                            <span className="text-lg font-bold text-white font-mono">{physicalScore.toFixed(1)}</span>
                            <div className="w-full bg-slate-800 h-1 mt-2 rounded-full overflow-hidden">
                                <div className="bg-blue-400 h-full" style={{ width: `${Math.min((physicalScore / totalTouchpoints) * 100, 100)}%` }}></div>
                            </div>
                        </div>

                        {/* Digital */}
                        <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50 flex flex-col items-center">
                            <span className="text-[10px] uppercase text-slate-500 font-bold mb-1">Digital</span>
                            <span className="text-lg font-bold text-white font-mono">{digitalScore.toFixed(1)}</span>
                             <div className="w-full bg-slate-800 h-1 mt-2 rounded-full overflow-hidden">
                                <div className="bg-purple-400 h-full" style={{ width: `${Math.min((digitalScore / totalTouchpoints) * 100, 100)}%` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        })()}
    </div>
)}

{/* --- NEW: CAMPAIGN HEALTH & RISK METRICS (DETAILED MANAGER VIEW) --- */}
<div className="panel p-0 bg-slate-800 border border-slate-700 shadow-sm rounded-lg overflow-hidden">
    
    {/* Header */}
    <div className="px-5 py-3 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center">
        <div className="flex items-center space-x-2">
            <Activity size={16} className="text-emerald-400"/>
            <h5 className="font-bold text-slate-200 text-sm">Campaign Health Indicators</h5>
        </div>
        <span className="text-[10px] text-slate-500 font-mono uppercase">Live Risk Assessment</span>
    </div>

    <div className="p-5 space-y-6">
        
        {/* 1. STRUCTURAL STABILITY (Variance) - FIXED LOGIC */}
        {(() => {
            const variance = results?.campaign_optimization?.optimal_budget_allocation?.portfolio_risk_variance ?? 1.0;
            
            // FIX: Invert the logic. 
            // In MPT, Variance of 1.0 = 100% Concentration (High Risk).
            // Variance < 0.6 = Good Diversification (High Stability).
            
            const isStable = variance < 0.65; // Diversified
            const isConcentrated = variance > 0.85; // Single Point of Failure
            
            // Visual Scale: 0.2 Variance = 100% Stability bar. 1.0 Variance = 0% Stability bar.
            const stabilityPct = Math.max(0, Math.min(100, (1.0 - variance) * 125)); 

            return (
                <div>
                    <div className="flex justify-between items-end mb-2">
                        <div>
                            <div className="text-xs font-bold text-white mb-0.5">Portfolio Stability</div>
                            <div className="text-[10px] text-slate-400">Risk Variance: <span className="font-mono text-slate-300">{variance.toFixed(3)}σ</span></div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${isStable ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : isConcentrated ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>
                            {isStable ? 'Diversified' : isConcentrated ? 'Critical Conc.' : 'Moderate'}
                        </span>
                    </div>

                    <div className="w-full bg-slate-700/30 rounded-full h-2 mb-3 overflow-hidden">
                        <div 
                            className={`h-full rounded-full transition-all duration-1000 ${isStable ? 'bg-gradient-to-r from-emerald-500 to-teal-400' : isConcentrated ? 'bg-gradient-to-r from-red-500 to-orange-400' : 'bg-gradient-to-r from-yellow-500 to-amber-400'}`} 
                            style={{ width: `${stabilityPct}%` }}
                        ></div>
                    </div>

                    {/* Analyst Insight */}
                    <div className="bg-slate-900/50 p-2.5 rounded border border-slate-700/50 flex gap-2">
                        <Info size={14} className="text-slate-500 mt-0.5 shrink-0"/>
                        <p className="text-[11px] text-slate-400 leading-snug">
                            <span className="font-semibold text-slate-300">Analyst Insight:</span> 
                            {isStable 
                                ? " Portfolio is well-diversified. Impact is protected against single-channel failure."
                                : isConcentrated
                                    ? " HIGH RISK. Allocation relies heavily on a single channel. Any friction there will collapse the campaign."
                                    : " Moderate concentration. Consider adding a secondary channel to hedge risk."}
                        </p>
                    </div>
                </div>
            );
        })()}

        <div className="border-t border-slate-700/50"></div>

        {/* 2. GRID: URGENCY & SCALABILITY */}
<div className="grid grid-cols-2 gap-4">
    
    {/* URGENCY (Theta) - FIX: Logic now matches backend thresholds correctly */}
    {(() => {
        const theta = results?.performance_dashboard?.campaign_scorecard?.metrics?.time_decay_theta ?? 
                      results?.campaign_optimization?.sophisticated_research_metrics?.theta_decay_sensitivity ?? -0.3;
        
        // Backend Logic: Theta < -50 is Critical, Theta < -10 is Alert
        const isCritical = theta < -50;
        const isUrgent = theta < -10; 
        
        return (
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-white">Voter Retention</span>
                    <Timer size={14} className={isCritical ? "text-red-500 animate-pulse" : isUrgent ? "text-rose-400" : "text-blue-400"}/>
                </div>
                
                <div className="flex items-baseline gap-1 mb-2">
                    <span className={`text-xl font-mono font-bold ${isCritical ? "text-red-500" : isUrgent ? "text-rose-400" : "text-white"}`}>
                        {theta.toFixed(2)}
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase">Decay Coeff.</span>
                </div>

                <div className="text-[10px] text-slate-400 leading-snug bg-slate-800/50 p-2 rounded border border-slate-700/50 h-20">
                    {isCritical 
                        ? <span className="text-red-400 font-bold">CRITICAL DECAY.</span>
                        : isUrgent 
                            ? <span className="text-rose-300 font-semibold">Decay Alert.</span> 
                            : <span className="text-blue-300 font-semibold">Stable Retention.</span>}
                    <br/>
                    {isCritical
                        ? "Immediate execution required. Voters are forgetting you daily."
                        : isUrgent 
                            ? "Support evaporates rapidly without daily contact."
                            : "Current support is sticky. You can afford gaps."}
                </div>
            </div>
        );
    })()}

    {/* SCALABILITY (Gamma) - FIX: Distinguishes between Saturation (Bad) and Acceleration (Good) */}
    {(() => {
        const gamma = results?.performance_dashboard?.campaign_scorecard?.metrics?.saturation_risk_gamma ?? 
                      results?.campaign_optimization?.sophisticated_research_metrics?.gamma_saturation_risk ?? -0.056;
        
        // Backend Logic: Gamma < -0.1 is Saturation. Gamma > 0.1 is Acceleration.
        const isSaturated = gamma < -0.1;
        const isAccelerating = gamma > 0.1;

        return (
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-white">Spend Scalability</span>
                    <TrendingUp size={14} className={isSaturated ? "text-yellow-400" : isAccelerating ? "text-emerald-400" : "text-slate-400"}/>
                </div>

                <div className="flex items-baseline gap-1 mb-2">
                    <span className={`text-xl font-mono font-bold ${isSaturated ? "text-yellow-400" : isAccelerating ? "text-emerald-400" : "text-white"}`}>
                        {gamma.toFixed(3)}
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase">Gamma</span>
                </div>

                <div className="text-[10px] text-slate-400 leading-snug bg-slate-800/50 p-2 rounded border border-slate-700/50 h-20">
                    {isSaturated 
                        ? <span className="text-yellow-300 font-semibold">Diminishing Returns.</span> 
                        : isAccelerating
                            ? <span className="text-emerald-300 font-semibold">Acceleration Phase.</span>
                            : <span className="text-slate-300 font-semibold">Linear Growth.</span>}
                    <br/>
                    {isSaturated 
                        ? "High saturation risk. Diversify channels immediately."
                        : isAccelerating
                            ? "High convexity. Increasing spend yields exponential returns."
                            : "Stable. Increasing budget yields linear vote gains."}
                </div>
            </div>
        );
    })()}
        </div>
    </div>
</div>

                        {/* --- VOTER RETENTION METER (DROP-IN) --- */}
{(() => {
    // 1. EXTRACT HIDDEN DATA
    const wards = results?.ward_level_analysis || {};
    const firstWardKey = Object.keys(wards)[0];
    const channelPerf = wards[firstWardKey]?.channel_performance || {};
    const firstChannelKey = Object.keys(channelPerf)[0];
    
    // Default to 1.0 (100% Retention) if data missing
    const decayEffect = channelPerf[firstChannelKey]?.contributing_factors?.diffusion_decay_effect ?? 1.0;
    
    // 2. CALCULATE METRICS
    const retentionPct = Math.min(100, Math.max(0, decayEffect * 100));
    const lossPct = 100 - retentionPct;
    
    // [FIX] USE GAP DAYS (Effective Distance) INSTEAD OF CALENDAR DAYS
    const rawDays = results?.input_summary?.days_until_election ?? 0;
    const gapDays = results?.input_summary?.gap_days ?? 0;
    // If we have a future target gap that is different from today, use that.
    const displayDays = (gapDays > 0 && gapDays < rawDays) ? gapDays : rawDays;

    // 3. DETERMINE STATUS & STYLES
    let status = { color: 'text-emerald-400', bar: 'bg-emerald-500', label: 'Excellent', icon: <CheckSquare size={14}/> };
    
    if (retentionPct < 30) {
        status = { color: 'text-red-400', bar: 'bg-red-500', label: 'Critical Decay', icon: <AlertTriangle size={14}/> };
    } else if (retentionPct < 70) {
        status = { color: 'text-yellow-400', bar: 'bg-yellow-500', label: 'Moderate Loss', icon: <Clock size={14}/> };
    }

    return (
        <div className="panel p-0 bg-slate-800 border border-slate-700 shadow-sm rounded-lg overflow-hidden mt-6 mb-6">
            {/* Header */}
            <div className="px-4 py-2 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center">
                <div className="flex items-center space-x-2">
                    <Clock size={16} className={status.color}/>
                    <h5 className="font-bold text-slate-200 text-sm">Voter Retention Rate</h5>
                </div>
                <span className="text-[10px] text-slate-500 font-mono uppercase">
                    {/* Updated Label Context */}
                    {gapDays > 0 && gapDays < rawDays ? "Effective Horizon" : "Time to Election"}: <span className="text-white font-bold">{displayDays} Days</span>
                </span>
            </div>

            <div className="p-4">
                {/* Main Metric */}
                <div className="flex items-end justify-between mb-2">
                    <div>
                        <div className={`text-2xl font-black font-mono ${status.color}`}>
                            {retentionPct.toFixed(1)}%
                        </div>
                        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                            Signal Retained
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-bold text-slate-500">
                            -{lossPct.toFixed(1)}%
                        </div>
                        <div className="text-[10px] text-slate-600 uppercase font-bold tracking-wider">
                            Decay Loss
                        </div>
                    </div>
                </div>

                {/* Visual Bar */}
                <div className="relative w-full h-3 bg-slate-700/50 rounded-full overflow-hidden mb-3">
                    {/* Retention Fill */}
                    <div 
                        className={`absolute left-0 top-0 bottom-0 ${status.bar} transition-all duration-1000 ease-out`} 
                        style={{ width: `${retentionPct}%` }}
                    >
                        {/* Shine Effect */}
                        <div className="absolute top-0 bottom-0 right-0 w-full bg-gradient-to-l from-white/20 to-transparent"></div>
                    </div>
                    
                    {/* Decay Pattern (Stripes) */}
                    <div className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" 
                         style={{ backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)', backgroundSize: '10px 10px' }}>
                    </div>
                </div>

                {/* Contextual Insight */}
                <div className="flex gap-2 items-start bg-slate-900/50 p-2 rounded border border-slate-700/50">
                    <div className={`mt-0.5 ${status.color}`}>
                        {status.icon}
                    </div>
                    <p className="text-[11px] text-slate-300 leading-snug">
                        <span className="font-semibold text-white">Impact Analysis: </span>
                        {retentionPct < 30 
                            ? "Townsley Decay is severe. >70% of current persuasion efforts will fade before election day. Switch to 'Maintenance Mode'."
                            : retentionPct < 70
                                ? "Significant signal loss detected. Requires reinforcement (re-contact) closer to polling day."
                                : "High retention window (Invernizzi Effect). Every contact made now is highly likely to convert into a vote."
                        }
                    </p>
                </div>
            </div>
        </div>
    );
})()}
                    
                    

                    {/* Untapped Potential (Budget Sensitivity) */}
                    <div className="panel p-4">
                        <div className="flex items-center space-x-2 mb-2">
                            <Diamond size={16} className="text-cyan-400"/>
                            <h5 className="font-semibold text-white">Untapped Potential</h5>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-300 mb-2">
                                {potentialLevel} 
                                {potentialLevel !== 'N/A' && <span className="text-base font-normal text-cyan-400/70 ml-2">({(potentialPercent).toFixed(1)}% Elasticity)</span>}
                            </div>
                            <p className="text-xs text-slate-300">{potentialRationale}</p>
                        </div>
                    </div>
                    
                    {/* Strategic Driver */}
                    <div className="panel p-4">
                        <div className="flex items-center space-x-2 mb-3"><BarChart size={16} className="text-green-400"/><h5 className="font-semibold text-white">Strategic Driver</h5></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-300 mb-2">{driver}</div>
                            <p className="text-xs text-slate-300">{driverRationale}</p>
                        </div>
                    </div>

                    {/* Average Fatigue KPI */}
                    <div className="panel p-4">
                        <div className="flex items-center space-x-2 mb-3"><Activity size={16} className="text-orange-400"/><h5 className="font-semibold text-white">Channel Fatigue</h5></div>
                        <div className="text-center">
                            <div className={`text-2xl font-bold mb-2 ${fatiguePercent > 40 ? 'text-orange-400' : 'text-slate-200'}`}>
                                {fatiguePercent.toFixed(0)}%
                            </div>
                            <div className="text-xs text-slate-300">
                                {fatiguePercent < 20 ? 
                                    "Minimal saturation. Multi-channel strategy is efficient." : 
                                 fatiguePercent > 40 ? 
                                    "High saturation detected. Returns diminishing due to channel density." :
                                    "Moderate fatigue. Monitor frequency."
                                }
                            </div>
                        </div>
                    </div>
                
                                    
                                    {/* Campaign Modifiers (Corrected for Effective Horizon) */}
                                        <div className="panel p-4">
                                            <div className="flex items-center space-x-2 mb-3"><Layers size={16} className="text-blue-400"/><h5 className="font-semibold text-white">Campaign Modifiers</h5></div>
                                            <ul className="space-y-3">
                                                <li className="flex items-start space-x-3"><div className="text-blue-400 pt-1"><Crosshair size={16} /></div><div><span className="text-xs text-slate-400">Focus</span><p className="text-sm font-medium text-white">{formatChannelName(results?.input_summary?.campaign_focus)}</p><p className="text-xs text-slate-300">Model is applying a positive weight to channels aligned with this strategy.</p></div></li>
                                                <li className="flex items-start space-x-3"><div className="text-blue-400 pt-1"><Flag size={16} /></div><div><span className="text-xs text-slate-400">Party</span><p className="text-sm font-medium text-white">{formatChannelName(results?.input_summary?.party)}</p><p className="text-xs text-slate-300">Applying adjustments based on party's historical demographic alignment.</p></div></li>
                                                
                                                {/* --- Enhanced Phase Dynamics (Future-Aware) --- */}
                                                <li className="flex items-start space-x-3">
                                                    <div className="text-blue-400 pt-1"><Activity size={16} /></div>
                                                    <div>
                                                        <span className="text-xs text-slate-400">Campaign Phase</span>
                                                        {(() => {
                                                            const rawDays = results?.input_summary?.days_until_election ?? 30;
                                                            const gapDays = results?.input_summary?.gap_days ?? 0;
                                                            
                                                            // [FIX] Use Effective Horizon if we have a future start date
                                                            const effectiveDays = (gapDays > 0 && gapDays < rawDays) ? gapDays : rawDays;
                                                            
                                                            const volatility = wardAnalysis?.demographic_intelligence?.demographic_volatility ?? 0.5;
                                                            const phase = getPhaseAnalysis(effectiveDays, volatility);
                                                            
                                                            return (
                                                                <>
                                                                    <p className="text-sm font-medium text-white">{phase.label}</p>
                                                                    <p className="text-xs text-slate-300">{phase.desc}</p>
                                                                    <div className="flex items-center space-x-2 mt-1">
                                                                        <span className={`text-[10px] px-1.5 rounded text-slate-300 ${gapDays > 0 && gapDays < rawDays ? 'bg-indigo-900/50 border border-indigo-500/30' : 'bg-slate-700'}`}>
                                                                            {effectiveDays} Days Left {gapDays > 0 && gapDays < rawDays ? '(Effective)' : ''}
                                                                        </span>
                                                                        {results?.input_summary?.electoral_shock && (
                                                                            <span className="text-[10px] bg-red-900/30 text-red-400 border border-red-900/50 px-1.5 rounded flex items-center gap-1">
                                                                                <Zap size={8}/> Active Shock
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                
                                    {/* Executive Summary */}
                                    <div className="panel p-4">
                                        <div className="flex items-center space-x-2 mb-3"><Lightbulb size={16} className="text-yellow-400"/><h5 className="font-semibold text-white">Executive Summary</h5></div>
                                        <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">{(results?.strategic_recommendations?.executive_summary ?? []).map((rec, i) => <li key={i}>{rec}</li>)}</ul>
                                    </div>
                
                                    {/* Actionable Insights */}
                                    <div className="panel p-4">
                                        <div className="flex items-center space-x-2 mb-3"><Zap size={16} className="text-success-400"/><h5 className="font-semibold text-white">Actionable Insights</h5></div>
                                        <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">
                                            {(tacticalRecs.length > 0 ? tacticalRecs : []).slice(0, 4).map((rec, i) => <li key={i}>{rec}</li>)}
                                            {tacticalRecs.length === 0 && <li>No actionable insights generated.</li>}
                                        </ul>
                                    </div>
                                    
                                    {/* Risk Management */}
                                    <div className="panel p-4">
                                        <div className="flex items-center space-x-2 mb-3"><Shield size={16} className="text-danger-400"/><h5 className="font-semibold text-white">Risk Management</h5></div>
                                        
                                        {monteCarloRisks ? (
                                            <div className="space-y-2 text-xs text-slate-300">
                                                <p className="font-medium text-white mb-1">Volatility Profile:</p>
                                                <ul className="list-disc list-inside space-y-1">
                                                    <li>Volatility Index: {((monteCarloRisks.volatility_index ?? 0)*100).toFixed(1)}%</li>
                                                    {monteCarloRisks.volatility_index > 0.15 ? (
                                                        <li><span className="text-yellow-400">High variance detected.</span> Diversify channels to mitigate risk.</li>
                                                    ) : (
                                                        <li>Stability is high. Projections are reliable.</li>
                                                    )}
                                                </ul>
                                            </div>
                                        ) : (<p className="text-xs text-slate-500 mb-4">No specific risks identified.</p>)}
                
                                        {confidenceWarnings.length > 0 && (
                                            <div className="border-t border-slate-700 pt-3 mt-3"><h6 className="text-sm font-semibold text-yellow-400 mb-2">Model Confidence Warnings</h6><ul className="space-y-2 text-xs text-yellow-300/80 list-disc list-inside">{confidenceWarnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul></div>
                                        )}
                                    </div>
                                    </div> 
            {/* --- END: Parent Grid --- */}

            {/* DELETE MODAL (PRESERVED) */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl shadow-red-900/20 transform scale-100 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 rounded-full bg-red-900/30 text-red-500 border border-red-500/20">
                                <Trash2 size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white">Delete Simulation?</h3>
                        </div>
                        
                        <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                            Are you sure you want to delete this simulation? This cannot be undone and all projection data will be lost.
                        </p>

                        <div className="flex gap-3">
                            <button 
                                onClick={() => setShowDeleteModal(false)} 
                                className="flex-1 py-2.5 rounded-lg border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleConfirmDelete} 
                                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-all shadow-lg shadow-red-900/30 border border-red-500 font-medium text-sm"
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
};